/* Abbara theme — global JS islands (vanilla, no React, no Babel).
 *
 * Wires up: scroll reveals, magnetic hover, custom cursor, click sounds,
 *           scroll-aware announce bar, page-curtain hash navigation off,
 *           nav scrolled state, theme toggle, currency menu, intro loader.
 *
 * Each behavior is opt-in via attribute or class — Liquid templates
 * just need to emit the right markup. Nothing reaches inside Shopify
 * objects; the file is content-agnostic so it survives section changes.
 */
(function () {
  'use strict';

  /* ── IntersectionObserver reveals ─────────────────────────
     Fire as soon as the element starts entering the viewport so
     the long, slow transition has time to play before the element
     is fully on-screen. Without this, the eye catches the animation
     mid-flight which reads as snappy. */
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.dataset.in = 'true';
          io.unobserve(e.target);
        }
      });
    },
    { threshold: 0.02, rootMargin: '0px 0px -8% 0px' }
  );
  const revealEls = document.querySelectorAll('.reveal, .reveal-img, [data-reveal]');
  revealEls.forEach((el) => io.observe(el));

  // IO is throttled when the tab loads in a background/hidden state, so
  // above-the-fold elements can stay invisible until the first scroll. This
  // fallback uses setTimeout (not throttled) to reveal anything already in
  // the viewport on the next tick.
  const flushVisible = () => {
    const vh = window.innerHeight;
    revealEls.forEach((el) => {
      if (el.dataset.in === 'true') return;
      const r = el.getBoundingClientRect();
      if (r.top < vh * 0.92 && r.bottom > 0) {
        el.dataset.in = 'true';
        io.unobserve(el);
      }
    });
  };
  setTimeout(flushVisible, 0);
  setTimeout(flushVisible, 200);
  window.addEventListener('scroll', flushVisible, { passive: true });
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) flushVisible();
  });

  /* ── Vibration API activation primer ───────────────────────
     Chrome requires user-activation before navigator.vibrate works.
     Per the HTML spec, only touchend / pointerup / click / mousedown
     grant activation for touch input — scroll and touchstart do NOT.
     We fire a SHORT subtle 4ms pulse on the very first qualifying
     event (or any plausible attempt) so that subsequent in-drag
     vibrates on the chip rail work from the first interaction. */
  let hapticPrimed = false;
  const primeHaptic = () => {
    if (hapticPrimed || !navigator.vibrate) return;
    // Only attempt the prime once the page actually has user activation.
    // Calling vibrate() before then triggers Chrome's console intervention
    // ("Blocked call to navigator.vibrate…"). userActivation gates it cleanly.
    if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
    hapticPrimed = true;
    try { navigator.vibrate(4); } catch (_) {}
  };
  // ONLY events that actually grant activation per the HTML spec. scroll and
  // touchstart do NOT grant it — priming on them is what logged the console
  // intervention — so they're deliberately excluded here. capture:true runs
  // us before any handler that might stop propagation.
  ['touchend', 'pointerup', 'click', 'pointerdown', 'mousedown'].forEach((evt) => {
    document.addEventListener(evt, primeHaptic, { capture: true, passive: true, once: true });
  });

  /* ── Global tap haptic ────────────────────────────────────
     Fires a short 8ms vibration on every tap of an important
     interactive element (hamburger, nav links, switchers, product
     cards, buttons, bundle/filter/sort controls). Regular page
     scrolling doesn't trigger this — only discrete clicks. iOS
     Safari ignores navigator.vibrate silently. */
  const HAPTIC_TARGETS = [
    '.nav__burger',
    '.nav__link',
    '.nav__icon',
    '.nav-drawer__links a',
    '.nav-drawer__util-link',
    '.nav-drawer__close',
    '.curreg-button', '.curreg-menu button',
    '.lang-switch__button', '.lang-switch__menu button',
    '.btn', '.btn--text',
    '.pcard',
    '.pcard__quick button', '.pcard__sizes button',
    '.bundle__hotspot',
    '.bundle__card-cta',
    '[data-bundle-add-all]',
    '[data-bundle-item] [data-remove]',
    '.plp-toolbar__filter',
    '[data-filters-toggle]', '[data-filters-close]',
    '.plp-sort__toggle',
    '.plp-sort__menu button',
    '.plp-chips__item',
    '[data-cart-open]', '[data-cart-close]',
    '[data-theme-toggle]',
    '[data-nav-toggle]',
    '.archive-card',
    '.chapter-tile',
    '.lookbook__shop-item',
  ].join(',');
  document.addEventListener('click', (e) => {
    if (!navigator.vibrate) return;
    if (e.target.closest(HAPTIC_TARGETS)) {
      try { navigator.vibrate(8); } catch (_) {}
    }
  }, { passive: true });

  /* ── Magnetic hover ─────────────────────────────────────── */
  document.querySelectorAll('.magnetic, [data-magnetic]').forEach((el) => {
    const strength = parseFloat(el.dataset.magnetic || el.dataset.strength || '0.3');
    el.addEventListener('mousemove', (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - (r.left + r.width / 2);
      const y = e.clientY - (r.top + r.height / 2);
      el.style.transform = `translate(${x * strength}px, ${y * strength}px)`;
    });
    el.addEventListener('mouseleave', () => {
      el.style.transform = 'translate(0,0)';
    });
  });

  /* ── Custom cursor ──────────────────────────────────────── */
  function initCursor() {
    if (matchMedia('(hover: none)').matches) return;
    const dot = document.createElement('div');
    const ring = document.createElement('div');
    dot.className = 'cursor-dot';
    ring.className = 'cursor-ring';
    document.body.appendChild(dot);
    document.body.appendChild(ring);
    let dx = 0, dy = 0, rx = 0, ry = 0, mx = 0, my = 0;
    document.addEventListener('mousemove', (e) => { mx = e.clientX; my = e.clientY; });
    (function tick() {
      dx += (mx - dx) * 0.35; dy += (my - dy) * 0.35;
      rx += (mx - rx) * 0.15; ry += (my - ry) * 0.15;
      dot.style.transform = `translate(${dx}px, ${dy}px) translate(-50%,-50%)`;
      ring.style.transform = `translate(${rx}px, ${ry}px) translate(-50%,-50%)`;
      requestAnimationFrame(tick);
    })();
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest('[data-cursor]');
      const state = t ? t.dataset.cursor : '';
      dot.dataset.state = state || '';
      ring.dataset.state = state || '';
    });
  }
  if (document.documentElement.dataset.cursor !== 'off') initCursor();

  /* ── Announce bar visibility + nav transparent/solid toggle ─── */
  const announceWrap = document.querySelector('.announce-wrap');
  const navEl = document.querySelector('header.nav');
  const navStartsTransparent = navEl && navEl.classList.contains('nav--transparent');
  // Initial-state schema setting: "hidden" reveals on scroll (default),
  // "visible" pins the bar visible from the first frame, "off" means
  // Liquid didn't render the wrap at all so this whole block no-ops.
  const announceInitial = announceWrap ? (announceWrap.dataset.initialState || 'hidden') : 'hidden';

  // Toggle .is-entering / .is-exiting so the keyframes play. The
  // resting visible / hidden states (with no class) are set by CSS
  // per data-exit. Each transition runs its keyframe once, and the
  // class is removed when the animation ends.
  //
  // Race-condition fix: AbortController cancels the previous
  // animationend listener when a new transition starts. Without
  // this, fast scroll-up-down cycles could leave a stale "exit"
  // listener registered that fires when the NEW (enter) animation
  // completes, incorrectly flipping dataset.visible back to false
  // and leaving the bar half-rendered / transparent.
  let bannerVisible = announceInitial === 'visible';
  let bannerAnimCtrl = null;
  if (announceWrap && bannerVisible) announceWrap.dataset.visible = 'true';

  const setBannerVisible = (next) => {
    if (next === bannerVisible) return;
    bannerVisible = next;
    if (!announceWrap) return;
    // Cancel any in-flight animationend listener from the previous transition.
    if (bannerAnimCtrl) bannerAnimCtrl.abort();
    bannerAnimCtrl = new AbortController();
    if (next) {
      announceWrap.classList.remove('is-exiting');
      announceWrap.classList.add('is-entering');
      announceWrap.dataset.visible = 'true';
      announceWrap.addEventListener('animationend', () => {
        announceWrap.classList.remove('is-entering');
      }, { once: true, signal: bannerAnimCtrl.signal });
    } else {
      announceWrap.classList.remove('is-entering');
      announceWrap.classList.add('is-exiting');
      announceWrap.addEventListener('animationend', () => {
        announceWrap.classList.remove('is-exiting');
        announceWrap.dataset.visible = 'false';
      }, { once: true, signal: bannerAnimCtrl.signal });
    }
  };

  const onScroll = () => {
    const scrollVisible = window.scrollY > Math.max(20, window.innerHeight * 0.03);
    // Force visible if merchant chose "Always visible"; otherwise use the scroll trigger.
    const visible = announceInitial === 'visible' ? true : scrollVisible;
    document.body.dataset.announce = visible ? 'true' : 'false';
    document.body.dataset.scrolled = window.scrollY > 20 ? 'true' : 'false';
    setBannerVisible(visible);
    // Only the home page nav starts transparent; remove transparency once
    // the user scrolls past the hero so text stays readable over any bg.
    if (navStartsTransparent && navEl) {
      if (window.scrollY > 20) navEl.classList.remove('nav--transparent');
      else navEl.classList.add('nav--transparent');
      // Crossfade white ↔ navy logo with the transparency state
      const transparent = navEl.classList.contains('nav--transparent');
      navEl.querySelectorAll('.nav__brand-img').forEach((img) => {
        const isWhite = img.dataset.variant === 'white';
        img.dataset.visible = (transparent === isWhite) ? 'true' : 'false';
      });
    }
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  /* ── Scroll marquee (announce + big marquee) ────────────── */
  document.querySelectorAll('[data-scroll-marquee]').forEach((wrap) => {
    const track = wrap.querySelector('.sm__track');
    if (!track) return;
    const base = parseFloat(wrap.dataset.scrollMarquee || '30');
    let lastY = window.scrollY, boost = 0, pos = 0;
    (function tick() {
      const dy = window.scrollY - lastY; lastY = window.scrollY;
      boost = Math.min(15, Math.max(-15, boost * 0.92 + dy * 0.6));
      pos -= (base + boost) / 60;
      const w = track.scrollWidth / 2;
      if (-pos >= w) pos = 0;
      if (pos > 0) pos -= w;
      track.style.transform = `translateX(${pos}px)`;
      requestAnimationFrame(tick);
    })();
  });

  /* ── Theme toggle (light/dark, persisted) ───────────────── */
  const setTheme = (t) => {
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem('voyage-theme', t);
  };
  setTheme(localStorage.getItem('voyage-theme') || 'light');
  document.querySelectorAll('[data-theme-toggle]').forEach((b) => {
    b.addEventListener('click', () => {
      const cur = document.documentElement.getAttribute('data-theme') || 'light';
      setTheme(cur === 'dark' ? 'light' : 'dark');
    });
  });

  /* ── Currency + language dropdowns ──────────────────────── */
  function bindDropdown(rootSel, toggleSel) {
    document.querySelectorAll(rootSel).forEach((root) => {
      const btn = root.querySelector(toggleSel);
      if (!btn) return;
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const open = root.dataset.open === 'true';
        // Close any other open dropdowns first
        document.querySelectorAll('[data-currency-switcher][data-open="true"], [data-lang-switch][data-open="true"]')
          .forEach((other) => { if (other !== root) other.dataset.open = 'false'; });
        root.dataset.open = open ? 'false' : 'true';
      });
      document.addEventListener('mousedown', (e) => {
        if (!root.contains(e.target)) root.dataset.open = 'false';
      });
    });
  }
  bindDropdown('[data-currency-switcher]', '[data-currency-toggle]');
  bindDropdown('[data-lang-switch]',       '[data-lang-toggle]');

  /* ── Intro loader — plays ONCE per session ────────────────
     Shows the full intro animation only on the FIRST page load of a
     visit; every later navigation (and reload) skips it, so the loader
     never doubles as a page-transition on internal clicks — independent
     of whether the page-transition curtain is on or off. A new tab or a
     new browser session shows it again (fresh sessionStorage). */
  const loader = document.querySelector('.loader');
  if (loader) {
    // The pre-paint head guard (in theme.liquid) sets the once-per-session flag
    // and hides the loader (display:none) on every load after the first. So here
    // we only run the timed reveal when it's actually visible; otherwise remove it.
    if (getComputedStyle(loader).display === 'none') {
      loader.remove();
    } else {
      // ~0.65s animation, then ~0.35s fade ≈ 1s total.
      setTimeout(() => {
        loader.dataset.done = 'true';
        setTimeout(() => loader.remove(), 350);
      }, 650);
    }
  }

  /* ── Cart drawer open/close ─────────────────────────────── */
  const drawer = document.querySelector('[data-cart-drawer]');
  const backdrop = document.querySelector('[data-cart-backdrop]');
  const openCart = () => {
    if (drawer) { drawer.dataset.open = 'true'; drawer.setAttribute('aria-hidden', 'false'); }
    if (backdrop) backdrop.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
  };
  const closeCart = () => {
    if (drawer) { drawer.dataset.open = 'false'; drawer.setAttribute('aria-hidden', 'true'); }
    if (backdrop) backdrop.dataset.open = 'false';
    document.body.style.overflow = '';
  };
  document.querySelectorAll('[data-cart-open]').forEach((b) => b.addEventListener('click', (e) => { e.preventDefault(); openCart(); }));
  // Expose so other modules (pdp.js sticky bar, bundle.js) can slide the cart
  // open after their own /cart/add.js POST — every add path should surface the
  // drawer so the shopper can check out without navigating away.
  window.openCart = openCart;
  window.closeCart = closeCart;
  // refreshCart() replaces the drawer's innerHTML on every add / qty change,
  // which destroys any close button bound directly. Delegate the close click
  // from document so [data-cart-close] keeps working after the drawer
  // re-renders. (The backdrop lives outside the drawer, so it can bind direct.)
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-close]')) closeCart();
  });
  if (backdrop) backdrop.addEventListener('click', closeCart);
  // Escape closes the cart drawer too (matches the nav drawer behaviour).
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.dataset.open === 'true') closeCart();
  });

  /* ── Mobile nav drawer (hamburger) ──────────────────────── */
  const navDrawer = document.querySelector('[data-nav-drawer]');
  const toggleNav = (force) => {
    if (!navDrawer) return;
    const willOpen = typeof force === 'boolean' ? force : navDrawer.dataset.open !== 'true';
    navDrawer.dataset.open = willOpen ? 'true' : 'false';
    navDrawer.setAttribute('aria-hidden', willOpen ? 'false' : 'true');
    document.body.dataset.navOpen = willOpen ? 'true' : 'false';
    document.querySelectorAll('[data-nav-toggle]').forEach((b) => b.setAttribute('aria-expanded', willOpen ? 'true' : 'false'));
  };
  document.querySelectorAll('[data-nav-toggle]').forEach((b) => b.addEventListener('click', () => toggleNav()));
  // Close drawer when navigating to a link inside it
  document.querySelectorAll('.nav-drawer__links a').forEach((a) => a.addEventListener('click', () => toggleNav(false)));
  // Close on Escape
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && navDrawer?.dataset.open === 'true') toggleNav(false); });

  /* ── Multi-level nav dropdowns ────────────────────────────
     Desktop: CSS handles :hover and :focus-within so the dropdown
     opens automatically. On touch devices that don't support hover,
     this JS toggles [data-open="true"] on the group when the parent
     link is tapped — the FIRST tap reveals the dropdown without
     navigating; the second tap (now on a child link OR on the
     same parent if the dropdown is already open) follows the link. */
  (function () {
    const groups = document.querySelectorAll('[data-nav-group]');
    if (!groups.length) return;

    // Touch detection — coarse pointer means tap-only device.
    const isTouch = window.matchMedia('(hover: none), (pointer: coarse)').matches;

    groups.forEach((group) => {
      const toggle = group.querySelector('[data-nav-group-toggle]');
      if (!toggle) return;

      if (isTouch) {
        toggle.addEventListener('click', (e) => {
          const isOpen = group.dataset.open === 'true';
          if (!isOpen) {
            // First tap: open dropdown, don't navigate.
            e.preventDefault();
            // Close any other open group.
            groups.forEach((g) => { if (g !== group) g.dataset.open = 'false'; });
            group.dataset.open = 'true';
            toggle.setAttribute('aria-expanded', 'true');
          }
          // Second tap: data-open already true → let the link follow.
        });
      } else {
        // Desktop: sync aria-expanded with hover/focus state.
        const sync = (open) => {
          toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        };
        group.addEventListener('mouseenter', () => sync(true));
        group.addEventListener('mouseleave', () => sync(false));
        group.addEventListener('focusin',  () => sync(true));
        group.addEventListener('focusout', () => sync(false));
      }
    });

    // Close all dropdowns when clicking outside or pressing Escape.
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-nav-group]')) {
        groups.forEach((g) => { g.dataset.open = 'false'; });
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') groups.forEach((g) => { g.dataset.open = 'false'; });
    });
  })();

  /* ── Add-to-cart (AJAX with form-POST fallback) ────────
     AJAX is preferred (instant, no reload). The native form-submit
     fallback exists ONLY for password-protected preview stores, where
     AJAX is blocked (401/403 or a bounce through /password). Real cart
     errors — sold out, max stock reached — must surface as an inline
     toast and keep the customer on the page; falling through to a
     native submit would land them on Shopify's raw /cart/add error
     screen ("The product X is already sold out"). */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();

    // Guard against a second press while the first add is still in flight —
    // the /cart/add.js round-trip can take ~1s, and without an immediate cue
    // shoppers press again and double-add.
    if (form.dataset.adding === 'true') return;
    form.dataset.adding = 'true';

    // Instant feedback the moment the button is pressed: disable it (so it
    // can't be pressed twice), swap its label to "Adding…", and toast right
    // away — so there's never a silent gap before the "Added" confirmation.
    const title = form.dataset.productTitle || 'item';
    const btn = form.querySelector('[type="submit"]') ||
                form.querySelector('button:not([type="button"])');
    const btnHTML = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.dataset.loading = 'true'; btn.innerHTML = 'Adding…'; }
    showToast('Adding ' + title + ' to cart…');

    const restore = () => {
      if (btn) { btn.disabled = false; btn.dataset.loading = 'false'; btn.innerHTML = btnHTML; }
      form.dataset.adding = 'false';
    };

    const fd = new FormData(form);
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      if (r.ok) {
        await refreshCart();
        // Keep the shopper browsing — do NOT open the drawer on add. Just close
        // the Quick View modal (if the add came from it) and let the toast +
        // the cart-count badge animation announce it. The drawer opens only
        // when the shopper clicks the cart icon.
        const qv = form.closest('[data-quickview]');
        if (qv) { qv.dataset.open = 'false'; qv.setAttribute('aria-hidden', 'true'); }
        showToast('Added to cart · ' + title);
        restore();
        return;
      }
      const gated = r.status === 401 || r.status === 403 ||
                    (r.url && r.url.indexOf('/password') !== -1);
      if (!gated) {
        // Genuine cart refusal (422 sold out, stock limit, etc.) —
        // show Shopify's own message inline and stay on the page.
        let msg = 'Could not add to bag — please try again';
        try {
          const j = await r.json();
          msg = j.description || j.message || msg;
        } catch (_) {}
        showToast(msg);
        restore();
        return;
      }
      console.warn('cart/add.js gated (', r.status, ') — falling back to native form submit');
    } catch (err) {
      // Network failure — a native submit would fail the same way, so
      // keep the customer on the page and let them retry.
      showToast('Network error — please try again');
      restore();
      return;
    }
    // Gated (password-protected dev store) → native submit below reloads the
    // page, so leave the button in its "Adding…" state; no restore needed.
    // Fallback (password-protected dev stores): full form submit with a
    // return_to back to the current page + a flag. The reloaded page detects
    // the flag and auto-opens the cart drawer + shows the toast.
    const back = new URL(window.location.href);
    back.searchParams.set('cart_added', '1');
    if (form.dataset.productTitle) back.searchParams.set('cart_added_title', form.dataset.productTitle);
    let returnInput = form.querySelector('input[name="return_to"]');
    if (!returnInput) {
      returnInput = document.createElement('input');
      returnInput.type = 'hidden';
      returnInput.name = 'return_to';
      form.appendChild(returnInput);
    }
    returnInput.value = back.pathname + back.search;
    form.submit();
  });

  /* ── Cart drawer line updates (qty +/− and remove) ─────
     The drawer's /cart/change forms used to submit natively, which
     navigated away from the drawer on every click (and could surface
     Shopify's raw error page). Intercept inside the drawer only —
     the /cart page keeps its native full-page behavior. */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action*="/cart/change"]');
    if (!form || !form.closest('[data-cart-drawer]')) return;
    e.preventDefault();
    const fd = new FormData(form);

    // A quantity of 0 means the line is being removed — whether via the ×
    // remove button or the − stepper stepping down from 1. Give the same
    // instant feedback as add-to-cart: fade the line, disable it so it can't
    // be pressed twice, and toast right away, then confirm on success.
    const isRemove = String(fd.get('quantity')) === '0';
    const article = form.closest('article');
    const title = (article && article.dataset.lineTitle) || 'item';
    if (isRemove) {
      if (article) {
        if (article.dataset.removing === 'true') return;
        article.dataset.removing = 'true';
        article.style.opacity = '0.4';
        article.style.pointerEvents = 'none';
      }
      showToast('Removing ' + title + '…');
    }

    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        body: fd,
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin',
      });
      if (r.ok) {
        await refreshCart();
        if (isRemove) showToast('Removed · ' + title);
        return;
      }
      let msg = 'Could not update bag';
      try {
        const j = await r.json();
        msg = j.description || j.message || msg;
      } catch (_) {}
      showToast(msg);
      if (article) { article.dataset.removing = 'false'; article.style.opacity = ''; article.style.pointerEvents = ''; }
    } catch (_) {
      showToast('Network error — please try again');
      if (article) { article.dataset.removing = 'false'; article.style.opacity = ''; article.style.pointerEvents = ''; }
    }
  });

  /* ── After a server-side add, detect the cart_added flag, refresh the
     count + show toast (NO auto-open), and strip the flag from the URL ── */
  (function () {
    const url = new URL(window.location.href);
    const added = url.searchParams.get('cart_added');
    if (!added) return;
    const title = url.searchParams.get('cart_added_title') || `${added} item${added === '1' ? '' : 's'}`;
    url.searchParams.delete('cart_added');
    url.searchParams.delete('cart_added_title');
    window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
    // Wait for the layout to settle, then announce (drawer stays closed)
    setTimeout(async () => {
      try {
        await refreshCart();
        showToast('Added to cart · ' + title);
      } catch (_) {}
    }, 300);
  })();

  async function refreshCart() {
    /* Use Shopify Section Rendering API: ?sections=cart-drawer
       lets us replace the drawer's innerHTML with fresh server-rendered markup. */
    try {
      const r = await fetch('/?sections=cart-drawer');
      const json = await r.json();
      const html = json['cart-drawer'];
      if (!html || !drawer) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('[data-cart-drawer]');
      if (fresh) drawer.innerHTML = fresh.innerHTML;
      // Re-apply the display-currency conversion to the freshly rendered prices.
      if (window.abbaraMoney) window.abbaraMoney.convert(drawer);
      // Update bag count badges + pulse animation
      const cart = await fetch('/cart.js').then((r) => r.json());
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        const prev = parseInt(el.textContent, 10) || 0;
        el.textContent = cart.item_count;
        el.dataset.empty = cart.item_count === 0 ? 'true' : 'false';
        if (cart.item_count > prev) {
          el.dataset.pulse = 'true';
          setTimeout(() => el.dataset.pulse = 'false', 700);
        }
      });
    } catch (err) { console.error('cart refresh failed', err); }
  }
  window.refreshCart = refreshCart;

  /* ── Offer popup — two-step capture (persuade → capture → success)
     Stage flow driven by `data-step` on the container. The dismissal
     is persisted to sessionStorage so the "won't appear again on
     this visit" loss-aversion line is honest. */
  (function () {
    const pop = document.querySelector('.offer-pop');
    if (!pop) return;

    // The newsletter form posts natively (Shopify customer form) and reloads
    // the page. If we've just come back from a successful/errored post, the
    // popup must show that result immediately — bypassing both the
    // once-per-session gate and the scroll trigger.
    const justPosted = !!pop.querySelector('.offer-pop__news-success, .offer-pop__news-error, [data-posted-ok], [data-posted-err]');

    // sessionStorage gate — once dismissed, never reopen this session.
    const DISMISS_KEY = 'voyage-offer-dismissed';
    try {
      if (!justPosted && sessionStorage.getItem(DISMISS_KEY) === 'true') return;
    } catch (_) { /* private mode etc. — fall through */ }

    let opened = false;
    let dismissed = false;

    const open = () => {
      if (opened || dismissed) return;
      opened = true;
      pop.dataset.open = 'true';
      // Reset to Step 1 each open (cheap defensive measure).
      pop.dataset.step = '1';
      document.body.style.overflow = 'hidden';
      const v = pop.querySelector('.offer-pop__video');
      const poster = pop.querySelector('.offer-pop__poster');
      if (v) {
        try {
          // Lazy-attach the video src here so the 7.9 MB popup-video.mp4
          // never gets fetched on initial page load. It's only requested
          // when the popup actually opens — major Lighthouse perf win.
          if (!v.src && v.dataset.lazySrc) {
            v.src = v.dataset.lazySrc;
            v.load();
          }
          // Also lazy-attach the hidden poster <img> so its 800w fetch is
          // deferred until popup-open. Without this, the browser pulls
          // ~250 KB on initial paint even though display:none.
          if (poster && !poster.src && poster.dataset.lazySrc) {
            poster.src = poster.dataset.lazySrc;
          }
          v.muted = true;
          v.defaultMuted = true;
          v.playsInline = true;
          if (v.readyState === 0) v.load();
          const p = v.play();
          if (p && p.catch) p.catch(() => {});
        } catch (_) {}
        // If the video isn't actually playing within 800ms (Low Power
        // Mode, autoplay block, etc.), hide it so the iOS play-button
        // overlay doesn't show. The poster image takes its place.
        setTimeout(() => {
          if (v.paused) {
            v.style.display = 'none';
            if (poster) poster.style.display = 'block';
          }
        }, 800);
      }
    };
    const close = () => {
      pop.dataset.open = 'false';
      dismissed = true;
      document.body.style.overflow = '';
      // Persist dismissal so the "won't appear again on this visit"
      // copy is truthful — navigation / refresh won't reopen the popup.
      try { sessionStorage.setItem(DISMISS_KEY, 'true'); } catch (_) {}
    };

    /* Triggers — the popup opens on whichever fires FIRST: a time delay,
       a scroll-depth %, or (desktop) exit-intent. All set in Theme
       settings → Offer popup. */
    const delayS  = parseFloat(pop.dataset.delay  || '0');   // seconds; 0 = off
    const scrollP = parseFloat(pop.dataset.scroll || '0');   // 0..100 %; 0 = off
    const exitOn  = pop.dataset.exit === 'true';
    const trigs = [];
    let popTimer = null;
    const teardown = () => { if (popTimer) clearTimeout(popTimer); trigs.forEach(([t, ev, fn]) => t.removeEventListener(ev, fn)); trigs.length = 0; };
    const fire = () => { if (opened || dismissed) return; open(); teardown(); };
    if (delayS > 0) popTimer = setTimeout(fire, delayS * 1000);
    if (scrollP > 0) {
      const onScr = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const pct = max > 0 ? (window.scrollY / max) * 100 : 100;
        if (pct >= scrollP) fire();
      };
      window.addEventListener('scroll', onScr, { passive: true }); trigs.push([window, 'scroll', onScr]); onScr();
    }
    if (exitOn && window.matchMedia('(hover:hover) and (pointer:fine)').matches) {
      const onExit = (e) => { if (!e.relatedTarget && (e.clientY || 0) <= 0) fire(); };
      document.addEventListener('mouseout', onExit); trigs.push([document, 'mouseout', onExit]);
    }
    // Fallback: if nothing is enabled, open after ~one viewport of scroll.
    if (delayS <= 0 && scrollP <= 0 && !exitOn) {
      const onFb = () => { if (window.scrollY > window.innerHeight * 0.6) fire(); };
      window.addEventListener('scroll', onFb, { passive: true }); trigs.push([window, 'scroll', onFb]);
    }

    pop.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', close));
    pop.addEventListener('click', (e) => { if (e.target === pop) close(); });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && pop.dataset.open === 'true') close();
    });

    // ── Stage flow — only present for the 'stages' (discount funnel) type.
    // Step 1 → 2 is JS; Stage 2 is a NATIVE Shopify signup that posts + reloads,
    // and on return the popup reopens at Stage 3 (see the justPosted handler below).
    if (pop.querySelector('.offer-pop__stage')) {
      const revealBtn = pop.querySelector('[data-reveal]');
      if (revealBtn) revealBtn.addEventListener('click', () => {
        pop.dataset.step = '2';
        const inp = pop.querySelector('.offer-pop__form input');
        if (inp) setTimeout(() => inp.focus({ preventScroll: true }), 60);
      });
    }

    // The newsletter form posts natively (Shopify customer form) — no submit
    // interception. If we've just returned from a successful or errored post,
    // open straight to that result (the session gate is bypassed above).
    if (justPosted) {
      fire();
      // Discount funnel: after a native signup, reopen at Stage 3 (confirmation)
      // — or Stage 2 if the submit came back with an error.
      if (pop.querySelector('.offer-pop__stage')) {
        pop.dataset.step = pop.querySelector('[data-posted-ok]') ? '3' : '2';
      }
    }
  })();

  /* ── PLP: view-mode picker + filter drawer ─────────────────
     View-mode replaces the old numeric density picker. Values:
       "2" / "3" / "4" → set --cols on .plp-grid, clear list mode
       "list"          → set data-view="list" on .plp-grid; CSS
                         then turns the grid into a stacked rows
                         layout (image left, meta right).
     Persists in localStorage under the legacy key "voyage-plp-density"
     so a user's prior preference upgrades to the new system without
     a re-pick. */
  (function () {
    const grid = document.querySelector('.plp-grid');
    if (!grid) {
      // No grid on this page (not the PLP) — only run the filter
      // drawer wiring below.
    } else {
      const buttons = document.querySelectorAll('.plp-view button[data-view]');
      const applyView = (value) => {
        if (value === 'list') {
          grid.dataset.view = 'list';
        } else {
          delete grid.dataset.view;
          grid.style.setProperty('--cols', value);
        }
        buttons.forEach((x) => x.dataset.active = x.dataset.view === value ? 'true' : 'false');
      };
      buttons.forEach((b) => {
        b.addEventListener('click', () => {
          const v = b.dataset.view;
          applyView(v);
          try { localStorage.setItem('voyage-plp-density', v); } catch (_) {}
        });
      });
      try {
        const saved = localStorage.getItem('voyage-plp-density');
        if (saved && (saved === 'list' || saved === '2' || saved === '3' || saved === '4')) {
          applyView(saved);
        }
      } catch (_) {}

      // "Colours" toggle — show one card per colour (both card sets are
      // server-rendered; we just flip a class, which also covers cards that
      // infinite scroll appends later).
      const colourBtn = document.querySelector('[data-view-colours]');
      if (colourBtn) {
        const setColours = (on, persist) => {
          grid.classList.toggle('plp-grid--by-colour', on);
          colourBtn.dataset.active = on ? 'true' : 'false';
          colourBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
          if (on) {
            // Expanded (by-colour) cards start display:none, so the scroll-reveal
            // observer never clears their image blur — reveal them explicitly so
            // they don't appear blurred-into-the-background (looking empty).
            grid.querySelectorAll('[data-card-expanded] .pcard__media-stack, [data-card-expanded] .pcard__media-stack img')
              .forEach((el) => el.classList.add('is-revealed'));
          }
          if (persist) { try { localStorage.setItem('abbara-plp-colours', on ? '1' : '0'); } catch (_) {} }
        };
        // A category view (?type=Shirts/Trousers/…) auto-enables the by-colour
        // layout. "All Pieces" (no type) uses the saved preference. Manual toggles
        // persist ONLY on All Pieces, so a category's auto-on never leaks across.
        const _type = new URLSearchParams(location.search).get('type');
        const _isCategory = _type && _type !== 'All Pieces' && _type !== 'All';
        colourBtn.addEventListener('click', () => setColours(!grid.classList.contains('plp-grid--by-colour'), !_isCategory));
        if (_isCategory) {
          setColours(true, false);
        } else {
          try { if (localStorage.getItem('abbara-plp-colours') === '1') setColours(true, false); } catch (_) {}
        }
      }

      // Category filter fallback — if the URL has ?type=X but Shopify didn't
      // actually narrow the product list, hide cards whose product type doesn't
      // match. Inline !important so it wins over the by-colour display rules.
      (function () {
        var t = (new URLSearchParams(location.search).get('type') || '').toLowerCase().trim();
        if (!t || t === 'all pieces' || t === 'all') return;
        grid.querySelectorAll('[data-ptype]').forEach(function (el) {
          if ((el.getAttribute('data-ptype') || '') !== t) el.classList.add('plp-type-hidden');
        });
      })();
    }
    const body = document.querySelector('.plp-body');
    const toggle = document.querySelector('[data-filters-toggle]');
    const mobileQ = matchMedia('(max-width: 900px)');
    const setOpen = (next) => {
      if (!body) return;
      body.dataset.filtersOpen = next ? 'true' : 'false';
      // Lock page scroll on mobile while the drawer is visible.
      if (mobileQ.matches) {
        document.body.dataset.filtersMobileOpen = next ? 'true' : 'false';
      }
    };
    if (body && toggle) {
      toggle.addEventListener('click', () => setOpen(body.dataset.filtersOpen !== 'true'));
      // Scrim tap and × button close the drawer.
      document.querySelectorAll('[data-filters-close]').forEach((b) => {
        b.addEventListener('click', () => setOpen(false));
      });
      // Esc closes on any breakpoint.
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && body.dataset.filtersOpen === 'true') setOpen(false);
      });
    }

    /* ── Client-side product-type filter ─────────────────────────────
       Shopify's storefront `filter.p.product_type=` query parameter
       requires "Product type" to be enabled as a filter in the store's
       admin (Online Store → Navigation → Filters), which it isn't on
       this store. The legacy `?type=` parameter has been deprecated.
       So we filter on the client: read the URL param, hide unmatching
       product cards, and update the count display.

       The pcard__sub element is server-rendered with the product type,
       which we use as the match key. Pagination is unaffected because
       this store has fewer products than the page size. */
    (function () {
      const params = new URLSearchParams(window.location.search);
      const typeFilter = (
        params.get('type') ||
        params.get('filter.p.product_type') ||
        ''
      ).trim();
      if (!typeFilter) return;
      const grid = document.querySelector('.plp-grid, .grid-products');
      if (!grid) return;
      let kept = 0;
      grid.querySelectorAll('.pcard').forEach((card) => {
        const sub = card.querySelector('.pcard__sub');
        const type = (sub?.textContent || '').trim();
        const wrapper = card.parentElement; // .reveal wrapper
        if (type.toLowerCase() === typeFilter.toLowerCase()) {
          kept++;
        } else if (wrapper) {
          wrapper.style.display = 'none';
        }
      });
      // Update the "16 pieces" count display.
      const countEl = document.querySelector('.plp-toolbar .tiny-num');
      if (countEl) {
        const word = kept === 1 ? 'piece' : 'pieces';
        countEl.textContent = `${kept} ${word}`;
      }
      // Mark the matching chip as active.
      document.querySelectorAll('.plp-chips__item').forEach((chip) => {
        const txt = chip.textContent.trim();
        if (txt.toLowerCase() === typeFilter.toLowerCase()) {
          chip.setAttribute('aria-current', 'page');
        } else {
          chip.removeAttribute('aria-current');
        }
      });
    })();

    /* ═══════════════════════════════════════════════════════════════
       BEGIN: Option B (JS-driven chip scroll on touch devices)
       ─────────────────────────────────────────────────────────────
       Why: On Chrome Android, a touchend that ends a NATIVE scroll
       gesture does NOT grant `navigator.vibrate()` activation —
       only "tap-like" touchends (no significant movement, OR
       movement that was preventDefault'd) grant it. So a normal
       chip swipe could never trigger haptic on the first try.

       Option B: take over the scrolling entirely. The container
       gets `overflow: hidden` and we translate `.plp-chips__inner`
       ourselves in response to touchmove. Because touchmove calls
       `e.preventDefault()`, Chrome doesn't classify the gesture as
       a scroll, so the resulting touchend grants activation — and
       vibrate works during AND after the very first swipe.

       iOS Safari still has no vibration API at all (Apple platform
       restriction). To compensate, we set `data-active-touch="true"`
       on whichever chip is centered under the user's finger during
       a swipe; the CSS provides a subtle color/scale tick so iOS
       users get equivalent VISUAL feedback as the rail moves.

       Revert: see assets/_backup-chip-scroll.js
       ═══════════════════════════════════════════════════════════ */
    const chipsWrap = document.querySelector('[data-chips-scroll]');
    const chipsEl = chipsWrap?.querySelector('.plp-chips');
    const innerRail = chipsEl?.querySelector('.plp-chips__inner');
    if (chipsEl && innerRail) {
      const TOL = 4;
      const isMobileTouch = matchMedia('(hover: none)').matches;

      // "Discovered" flag — survives within a tab session. The one-time
      // nudge animation only runs if the user has NEVER interacted with
      // the chip rail before in this session. Any chip click, any swipe,
      // or even landing on an already-filtered URL marks it discovered.
      const DISCOVERED_KEY = 'voyage-chips-discovered';
      const isDiscovered = () => {
        try { return sessionStorage.getItem(DISCOVERED_KEY) === 'true'; }
        catch (_) { return false; }
      };
      const markDiscovered = () => {
        try { sessionStorage.setItem(DISCOVERED_KEY, 'true'); } catch (_) {}
      };
      // Already on a filtered URL? They've used chips before → no nudge.
      if (/[?&](type|filter\.p\.product_type)=/.test(window.location.search)) {
        markDiscovered();
      }
      // Any chip click marks discovered (covers all branches/devices).
      chipsEl.addEventListener('click', (e) => {
        if (e.target.closest('.plp-chips__item')) markDiscovered();
      }, { passive: true });
      // Option B's preventDefault'd touchmove can interfere with iOS
      // Safari's click dispatch (chip <a> taps stop navigating). Since
      // iOS has no Vibration API anyway, restricting Option B to
      // platforms that actually need it (Android-class browsers that
      // expose navigator.vibrate) keeps iOS clicks working natively.
      const useOptionB = isMobileTouch && typeof navigator.vibrate === 'function';

      const padHorizontal = () =>
        parseFloat(getComputedStyle(chipsEl).paddingLeft || '0') +
        parseFloat(getComputedStyle(chipsEl).paddingRight || '0');

      // ── data-active-touch helpers (used by both touch branches) ──
      let activeChip = null;
      const setActiveTouch = (chip) => {
        if (activeChip === chip) return;
        if (activeChip) delete activeChip.dataset.activeTouch;
        if (chip) chip.dataset.activeTouch = 'true';
        activeChip = chip;
      };
      const updateActiveFromCenter = () => {
        const r = chipsEl.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        let closest = null;
        let closestDist = Infinity;
        innerRail.querySelectorAll('.plp-chips__item').forEach((chip) => {
          const cr = chip.getBoundingClientRect();
          const d = Math.abs(cr.left + cr.width / 2 - centerX);
          if (d < closestDist) { closest = chip; closestDist = d; }
        });
        setActiveTouch(closest);
      };

      if (!useOptionB) {
        // ── Desktop / iOS: native horizontal scroll, no JS override ──
        innerRail.style.transform = '';
        chipsEl.style.overflowX = 'auto';
        const updateNative = () => {
          const max = chipsEl.scrollWidth - chipsEl.clientWidth;
          chipsWrap.dataset.atStart = chipsEl.scrollLeft <= TOL ? 'true' : 'false';
          chipsWrap.dataset.atEnd = chipsEl.scrollLeft >= max - TOL ? 'true' : 'false';
          if (max <= TOL) {
            chipsWrap.dataset.atStart = 'true';
            chipsWrap.dataset.atEnd = 'true';
          }
        };
        updateNative();
        chipsEl.addEventListener('scroll', updateNative, { passive: true });
        window.addEventListener('resize', updateNative, { passive: true });

        // Auto-center the active chip on page load — smoothly. A small
        // delay lets layout/fonts settle so the measurement is accurate.
        setTimeout(() => {
          const active = chipsEl.querySelector('[aria-current="page"]');
          if (!active) return;
          const railRect = chipsEl.getBoundingClientRect();
          const chipRect = active.getBoundingClientRect();
          const target =
            chipsEl.scrollLeft +
            (chipRect.left - railRect.left) +
            chipRect.width / 2 -
            railRect.width / 2;
          chipsEl.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }, 160);

        // iOS visual feedback during native scroll — substitute for the
        // missing Vibration API. We listen passively (NEVER call
        // preventDefault) so native scroll and link clicks behave
        // exactly as Safari expects.
        if (isMobileTouch) {
          let touching = false;
          chipsEl.addEventListener('touchstart', () => { touching = true; }, { passive: true });
          const onTouchScroll = () => { if (touching) updateActiveFromCenter(); };
          chipsEl.addEventListener('scroll', onTouchScroll, { passive: true });
          chipsEl.addEventListener('touchmove', () => { if (touching) updateActiveFromCenter(); }, { passive: true });
          const endTouch = () => { touching = false; setActiveTouch(null); };
          chipsEl.addEventListener('touchend', endTouch, { passive: true });
          chipsEl.addEventListener('touchcancel', endTouch, { passive: true });

          // Horizontal boomerang on iOS — Android already gets this via
          // Option B. We lock direction (h vs v) on the first 10px of
          // movement; only horizontal-dominant gestures enter over-drag.
          // Vertical swipes pass straight through to native page scroll.
          let dragStartX = 0;
          let dragStartY = 0;
          let overDragStartX = 0;
          let overDragging = false;
          let direction = null; // 'h' | 'v' | null
          const elastic = (over) => 80 * (1 - 1 / (1 + over / 80));
          chipsEl.addEventListener('touchstart', (e) => {
            if (e.touches.length !== 1) return;
            dragStartX = e.touches[0].clientX;
            dragStartY = e.touches[0].clientY;
            direction = null;
            overDragging = false;
            innerRail.style.transition = '';
          }, { passive: true });
          chipsEl.addEventListener('touchmove', (e) => {
            if (e.touches.length !== 1) return;
            const cx = e.touches[0].clientX;
            const cy = e.touches[0].clientY;
            const dx = cx - dragStartX;
            const dy = cy - dragStartY;
            // Lock direction after a small threshold so taps and tiny
            // movements stay neutral. Once locked vertical, this gesture
            // is OUT of our scope — native page scroll handles it.
            if (direction === null) {
              if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
                direction = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
              }
            }
            if (direction !== 'h') return;
            const max = chipsEl.scrollWidth - chipsEl.clientWidth;
            const atLeft = chipsEl.scrollLeft <= 0;
            const atRight = chipsEl.scrollLeft >= max;
            if (!overDragging) {
              if (atLeft && dx > 6) {
                overDragging = true; overDragStartX = cx;
              } else if (atRight && dx < -6) {
                overDragging = true; overDragStartX = cx;
              }
            }
            if (overDragging) {
              chipsEl.scrollLeft = atLeft ? 0 : max;
              const overDx = cx - overDragStartX;
              const sign = overDx < 0 ? -1 : 1;
              innerRail.style.transform = `translateX(${sign * elastic(Math.abs(overDx))}px)`;
              e.preventDefault();
            }
          }, { passive: false });
          const endOverdrag = () => {
            direction = null;
            if (!overDragging) return;
            overDragging = false;
            innerRail.style.transition = 'transform .55s cubic-bezier(.18, 1.35, .5, 1)';
            innerRail.style.transform = 'translateX(0)';
            setTimeout(() => { innerRail.style.transition = ''; }, 600);
          };
          chipsEl.addEventListener('touchend', endOverdrag, { passive: true });
          chipsEl.addEventListener('touchcancel', endOverdrag, { passive: true });
        }
      } else {
        // ── Touch: take over scrolling entirely ──
        chipsEl.style.overflowX = 'hidden';

        let scrollX = 0;
        let dragging = false;
        let direction = null;       // 'h' | 'v' | null
        let didSwipe = false;
        let dragStartX = 0;
        let dragStartY = 0;
        let dragStartScrollX = 0;
        let lastMoveX = 0;
        let lastMoveTime = 0;
        let velocity = 0;
        let momentumRAF = 0;
        let snapRAF = 0;
        let nudgeRAF = 0;
        let lastHapticAt = 0;

        const getMax = () =>
          Math.max(0, innerRail.scrollWidth - (chipsEl.clientWidth - padHorizontal()));

        const updateEdges = () => {
          const max = getMax();
          chipsWrap.dataset.atStart = scrollX <= TOL ? 'true' : 'false';
          chipsWrap.dataset.atEnd = scrollX >= max - TOL ? 'true' : 'false';
          if (max <= TOL) {
            chipsWrap.dataset.atStart = 'true';
            chipsWrap.dataset.atEnd = 'true';
          }
        };
        const applyTransform = () => {
          innerRail.style.transform = `translateX(${-scrollX}px)`;
          updateEdges();
        };
        applyTransform();
        window.addEventListener('resize', applyTransform, { passive: true });

        // Auto-center the active chip on page load — animated via the
        // existing snap-to helper for a smooth ease-out flow rather
        // than an instant jump.
        setTimeout(() => {
          const active = innerRail.querySelector('[aria-current="page"]');
          if (!active) return;
          const railRect = chipsEl.getBoundingClientRect();
          const chipRect = active.getBoundingClientRect();
          const delta =
            chipRect.left + chipRect.width / 2 -
            (railRect.left + railRect.width / 2);
          const max = getMax();
          const target = Math.max(0, Math.min(scrollX + delta, max));
          if (Math.abs(target - scrollX) < 2) return;
          // animateTo: same easing as snapTo, no haptic on this passive
          // auto-positioning.
          const start = scrollX;
          const t0 = performance.now();
          const duration = 600;
          const tick = (now) => {
            const t = Math.min(1, (now - t0) / duration);
            const eased = 1 - Math.pow(1 - t, 2.6);
            scrollX = start + (target - start) * eased;
            applyTransform();
            if (t < 1) snapRAF = requestAnimationFrame(tick);
          };
          snapRAF = requestAnimationFrame(tick);
        }, 160);

        const elastic = (overflow) => 80 * (1 - 1 / (1 + overflow / 80));

        const tryDragHaptic = () => {
          if (!navigator.vibrate) return;
          const now = Date.now();
          const interval = Math.max(30, 200 / (1 + Math.abs(velocity)));
          if (now - lastHapticAt >= interval) {
            try { navigator.vibrate(8); } catch (_) {}
            lastHapticAt = now;
          }
        };

        const snapTo = (target, withSpringHaptic) => {
          cancelAnimationFrame(snapRAF);
          cancelAnimationFrame(momentumRAF);
          const start = scrollX;
          const startTime = performance.now();
          const duration = 550;
          if (withSpringHaptic && navigator.vibrate && Math.abs(start - target) > 4) {
            try { navigator.vibrate([18, 60, 12, 90, 8, 130]); } catch (_) {}
          }
          const tick = (now) => {
            const t = Math.min(1, (now - startTime) / duration);
            const eased = 1 - Math.pow(1 - t, 2.6);
            scrollX = start + (target - start) * eased;
            applyTransform();
            if (t < 1) snapRAF = requestAnimationFrame(tick);
            else { scrollX = target; applyTransform(); setActiveTouch(null); }
          };
          snapRAF = requestAnimationFrame(tick);
        };

        const animateMomentum = () => {
          cancelAnimationFrame(momentumRAF);
          const max = getMax();
          const friction = 0.94;
          const tick = () => {
            scrollX += velocity * 16;
            if (scrollX < 0 || scrollX > max) {
              velocity *= 0.78;
            } else {
              velocity *= friction;
            }
            applyTransform();
            if (Math.abs(velocity) > 0.04 && scrollX >= 0 && scrollX <= max) {
              momentumRAF = requestAnimationFrame(tick);
            } else if (scrollX < 0) snapTo(0, true);
            else if (scrollX > max) snapTo(max, true);
            else setActiveTouch(null);
          };
          momentumRAF = requestAnimationFrame(tick);
        };

        chipsEl.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;
          cancelAnimationFrame(momentumRAF);
          cancelAnimationFrame(snapRAF);
          cancelAnimationFrame(nudgeRAF);
          dragging = true;
          direction = null;
          didSwipe = false;
          dragStartX = e.touches[0].clientX;
          dragStartY = e.touches[0].clientY;
          dragStartScrollX = scrollX;
          lastMoveX = dragStartX;
          lastMoveTime = Date.now();
          velocity = 0;
        }, { passive: false });

        chipsEl.addEventListener('touchmove', (e) => {
          if (!dragging || e.touches.length !== 1) return;
          const cx = e.touches[0].clientX;
          const cy = e.touches[0].clientY;
          const dx = cx - dragStartX;
          const dy = cy - dragStartY;
          if (direction === null) {
            // 14px threshold — large enough that ordinary taps (with
            // finger wobble) aren't misclassified as drags, which would
            // call e.preventDefault() and stop the natural click from
            // firing on the chip <a>. Below this threshold we let the
            // gesture stay "tappable".
            if (Math.abs(dx) > 14 || Math.abs(dy) > 14) {
              direction = Math.abs(dx) > Math.abs(dy) ? 'h' : 'v';
            }
          }
          if (direction !== 'h') return;
          // Horizontal drag — take over from native scroll. preventDefault
          // is what makes Chrome classify the gesture as non-scroll so
          // the eventual touchend grants vibrate activation.
          e.preventDefault();
          if (!didSwipe) markDiscovered();
          didSwipe = true;
          const max = getMax();
          let newScroll = dragStartScrollX - dx;
          if (newScroll < 0) newScroll = -elastic(-newScroll);
          else if (newScroll > max) newScroll = max + elastic(newScroll - max);
          scrollX = newScroll;
          applyTransform();
          updateActiveFromCenter();
          const now = Date.now();
          const dt = now - lastMoveTime;
          if (dt > 0) velocity = (lastMoveX - cx) / dt;
          lastMoveX = cx;
          lastMoveTime = now;
          tryDragHaptic();
        }, { passive: false });

        const endDrag = (e) => {
          if (!dragging) return;
          dragging = false;
          if (!didSwipe) {
            // Tap fallback — even though we didn't preventDefault for this
            // gesture (movement stayed below threshold), Chrome can still
            // suppress the synthetic click after non-passive touch
            // listeners run on Android. Manually navigate to the tapped
            // chip's href so a tap ALWAYS works.
            if (e && e.changedTouches && e.changedTouches[0]) {
              const t = e.changedTouches[0];
              const target = document.elementFromPoint(t.clientX, t.clientY);
              const chip = target?.closest('.plp-chips__item');
              if (chip && chip.tagName === 'A' && chip.href) {
                if (navigator.vibrate) {
                  try { navigator.vibrate(8); } catch (_) {}
                }
                window.location.href = chip.href;
                return;
              }
            }
            setActiveTouch(null);
            return;
          }
          // The preventDefault'd touchmove sequence made this touchend an
          // activation-granting event. Use a single 8ms tick at release.
          if (navigator.vibrate) {
            try { navigator.vibrate(8); } catch (_) {}
          }
          const max = getMax();
          if (scrollX < 0 || scrollX > max) {
            snapTo(scrollX < 0 ? 0 : max, true);
          } else if (Math.abs(velocity) > 0.15) {
            animateMomentum();
          } else {
            setActiveTouch(null);
          }
        };
        chipsEl.addEventListener('touchend', endDrag, { passive: false });
        chipsEl.addEventListener('touchcancel', endDrag, { passive: false });

        // Subtle one-time discovery nudge — content slides 6px right→left
        // and stays there (no swing back). Plays ONLY on the first visit
        // of the tab session — once the user has clicked any chip OR
        // swiped the rail, the session is marked discovered and the
        // nudge never runs again.
        setTimeout(() => {
          if (didSwipe || isDiscovered()) return;
          const max = getMax();
          if (max <= TOL) return;
          const target = 6;
          const start = scrollX;
          const t0 = performance.now();
          const tick = (now) => {
            const t = Math.min(1, (now - t0) / 500);
            const eased = 1 - Math.pow(1 - t, 2);
            scrollX = start + (target - start) * eased;
            applyTransform();
            if (t < 1 && !didSwipe) nudgeRAF = requestAnimationFrame(tick);
          };
          nudgeRAF = requestAnimationFrame(tick);
        }, 1200);
      }
    }
    /* END: Option B */

    // Custom sort dropdown — replaces native <select> so the menu
    // matches the theme on every OS.
    const sortForm = document.querySelector('[data-sort-form]');
    if (sortForm) {
      const toggle = sortForm.querySelector('[data-sort-toggle]');
      const menu = sortForm.querySelector('[data-sort-menu]');
      const input = sortForm.querySelector('[data-sort-input]');
      const labelEl = sortForm.querySelector('[data-sort-label]');
      const closeMenu = () => {
        menu.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
      };
      const openMenu = () => {
        menu.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
      };
      toggle.addEventListener('click', () => {
        menu.hidden ? openMenu() : closeMenu();
      });
      // Option click: update hidden input + visible label, then submit
      sortForm.querySelectorAll('[data-sort-option]').forEach((opt) => {
        opt.addEventListener('click', () => {
          const value = opt.dataset.sortOption;
          const name = opt.dataset.sortName;
          input.value = value;
          if (labelEl) labelEl.textContent = name;
          sortForm.querySelectorAll('[data-sort-option]').forEach((o) => {
            o.dataset.active = o === opt ? 'true' : 'false';
          });
          closeMenu();
          sortForm.submit();
        });
      });
      // Outside click + Esc close
      document.addEventListener('click', (e) => {
        if (menu.hidden) return;
        if (!sortForm.contains(e.target)) closeMenu();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !menu.hidden) closeMenu();
      });
    }

    // Sticky bar — visible whenever scrolled past its inline position,
    // back to inline at its natural location. CSS `position: sticky`
    // handles all the layout; JS only toggles a `floating` data-state
    // so we can fade in a subtle shadow when the bar has actually
    // detached from its inline position.
    const stickyBar = document.querySelector('[data-plp-sticky]');
    if (stickyBar) {
      const onScroll = () => {
        const r = stickyBar.getBoundingClientRect();
        const navH = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--nav-h') || '70', 10);
        stickyBar.dataset.state = r.top <= navH + 1 ? 'floating' : 'inline';
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }
  })();

  /* ── Product card hover video ────────────────────────────
     Desktop (pointer that hovers): play on mouseenter.
     Mobile / touch (no hover): play when the card is in the central
     band of the viewport while the user scrolls. */
  /* Product card hover video.
     iOS Safari is the strictest browser for inline autoplay — it
     requires muted+playsInline set as JS PROPERTIES (not just HTML
     attributes), needs load() before play() when preload="none",
     and throws on currentTime if metadata isn't loaded. */
  const isTouch = matchMedia('(hover: none)').matches;
  const prepCardVideo = (v) => {
    // iOS reads these as runtime properties; setting them as HTML
    // attributes alone isn't always enough after dynamic activation.
    v.muted = true;
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
  };
  const startCardVideo = (v) => {
    v.dataset.playing = 'true';
    prepCardVideo(v);
    // preload="none" means readyState=0. iOS needs an explicit load()
    // before play() will resolve.
    if (v.readyState === 0) {
      try { v.load(); } catch (_) {}
    }
    const p = v.play();
    if (p && p.catch) p.catch(() => {});
    try { if (v.readyState >= 1) v.currentTime = 0; } catch (_) {}
  };
  const stopCardVideo = (v) => {
    v.dataset.playing = 'false';
    try { v.pause(); } catch (_) {}
    try { if (v.readyState >= 1) v.currentTime = 0; } catch (_) {}
  };

  // Global iOS autoplay rescue + fallback hiding.
  // - retryAutoplay: on first touchend/click, retry every paused
  //   autoplay video. iOS often refuses autoplay before any user
  //   gesture; after the first one, things start working.
  // - hideFailedAutoplay: 1.6s after init AND after every retry,
  //   any video that's STILL paused gets `display: none`. This
  //   eliminates the iOS Safari play-button overlay (which appears
  //   whenever autoplay fails — including in Low Power Mode where
  //   autoplay is OS-blocked entirely). The poster image / fallback
  //   <img> sibling stays visible in its place.
  const hideFailedVideo = (v) => {
    if (!v.paused) return;
    v.style.display = 'none';
    v.dataset.failed = 'true';
    // For the popup, show the dedicated poster image
    const popPoster = v.parentElement?.querySelector('.offer-pop__poster');
    if (popPoster) popPoster.style.display = 'block';
  };
  const sweepAutoplay = () => {
    document.querySelectorAll('video[autoplay]').forEach(hideFailedVideo);
  };
  const retryAutoplay = () => {
    document.querySelectorAll('video').forEach((v) => {
      if (!v.paused) return;
      if (!v.hasAttribute('autoplay') && v.dataset.playing !== 'true') return;
      try {
        v.muted = true;
        v.playsInline = true;
        const p = v.play();
        if (p && p.then) {
          p.catch(() => {
            // Retry failed too — hide so the play overlay disappears
            hideFailedVideo(v);
          });
        }
      } catch (_) { hideFailedVideo(v); }
    });
  };
  document.addEventListener('touchend', retryAutoplay, { capture: true, passive: true, once: true });
  document.addEventListener('click', retryAutoplay, { capture: true, passive: true, once: true });
  // Initial sweep — give Safari a chance to start each video.
  setTimeout(sweepAutoplay, 1600);
  // Second sweep after the retry window (user has had ~3s to tap something).
  setTimeout(sweepAutoplay, 3500);
  document.querySelectorAll('.pcard').forEach((card) => {
    const v = card.querySelector('[data-pcard-video]');
    if (!v) return;
    if (!isTouch) {
      card.addEventListener('mouseenter', () => startCardVideo(v));
      card.addEventListener('mouseleave', () => stopCardVideo(v));
    }
  });

  if (isTouch) {
    // Trigger band: 10% tall, centered 10% above the viewport's true
    // center (so the card the user is naturally focused on plays).
    const pcardIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          const v = e.target.querySelector('[data-pcard-video]');
          if (!v) return;
          if (e.isIntersecting) startCardVideo(v);
          else stopCardVideo(v);
        });
      },
      { threshold: 0.01, rootMargin: '-35% 0px -55% 0px' }
    );
    document.querySelectorAll('.pcard').forEach((card) => {
      if (card.querySelector('[data-pcard-video]')) pcardIO.observe(card);
    });
  }

  /* ── Toast ──────────────────────────────────────────────── */
  function showToast(msg) {
    let toast = document.querySelector('.toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.dataset.show = 'true';
    clearTimeout(showToast._t);
    showToast._t = setTimeout(() => { toast.dataset.show = 'false'; }, 2200);
  }
  window.abbaraToast = showToast;

  /* ═══════════════════════════════════════════════════════════
     EDITORIAL POLISH — folio counter, curtain transition,
     time-of-day greeting. Considered detail set.
     ═══════════════════════════════════════════════════════════ */

  /* ── Folio counter ─────────────────────────────────────────
     "01 / 07" indicator updates as user scrolls past sections.
     Uses the existing `data-screen-label` attributes that several
     sections already emit (home-hero: "01 Hero", etc.). */
  (function () {
    const counter = document.querySelector('[data-folio-counter]');
    if (!counter) return;
    const nEl = counter.querySelector('[data-folio-n]');
    const totalEl = counter.querySelector('[data-folio-total]');
    const sections = Array.from(document.querySelectorAll('[data-screen-label]'));
    if (!sections.length) return;
    // Pad to 2 digits ("3" → "03") matching the editorial style.
    const pad = (n) => String(n).padStart(2, '0');
    totalEl.textContent = pad(sections.length);
    let current = -1;
    const setCurrent = (i) => {
      if (i === current) return;
      current = i;
      nEl.textContent = pad(i + 1);
    };
    setCurrent(0);
    // Fire counter visible after initial scroll past hero.
    const onScroll = () => {
      const vy = window.innerHeight * 0.45;
      let activeIdx = 0;
      sections.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        if (r.top <= vy && r.bottom >= 60) activeIdx = i;
      });
      setCurrent(activeIdx);
      counter.dataset.visible = window.scrollY > 100 ? 'true' : 'false';
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  })();

  /* ── Page-transition curtain ───────────────────────────────
     Cinematic theatre-curtain between pages. The hand-off:
     (1) Click triggers `data-state="rising"` on the outgoing page.
     (2) Just before navigation, sessionStorage sets a "pending" flag.
     (3) On the new page, layout/theme.liquid's pre-paint inline
         <script> reads that flag and adds `.curtain-incoming` to
         <html> BEFORE first paint, pinning the curtain at
         translateY(0) so the page renders already covered (no jump).
     (4) Here, on DOMContentLoaded, we wait two animation frames so
         the browser has paint-committed the covered state, THEN
         trigger `data-state="falling"` and remove the seed class.
         The fall animates from 0 → -100% smoothly, no snap.
     Cmd/Ctrl/middle-click, hash anchors, downloads, mailto/tel,
     and target=_blank links are all skipped. */
  (function () {
    const curtain = document.querySelector('[data-curtain]');
    if (!curtain) return;
    const html = document.documentElement;
    if (html.classList.contains('curtain-incoming')) {
      // Two RAFs let the browser commit the covered state with the
      // navy bridge in place. A 180ms hold lets fonts and images
      // paint underneath the curtain (so the eventual reveal isn't
      // half-loaded). Then a CAREFULLY ORDERED hand-off:
      //
      // 1. Set data-state="falling" → animation queued, the keyframes
      //    explicitly start at translateY(0) so the element is pinned
      //    there for the entire animation.
      // 2. Only THEN remove .curtain-incoming and the bridge. The
      //    falling animation overrides the static transform, so even
      //    though the base .curtain rule says translateY(100%), the
      //    animation keeps it at translateY(0) at frame 0 — no jump.
      // 3. The curtain's own navy background covers everything during
      //    the fall, so removing the body/html navy bridge underneath
      //    is invisible.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            // STEP 1: queue the falling animation first.
            curtain.dataset.state = 'falling';
            sessionStorage.removeItem('voyage-curtain-pending');
            // STEP 2: drop the bridge — happens in the SAME task so
            // the browser commits both atomically. The opaque curtain
            // covers the page underneath through the whole fall.
            html.classList.remove('curtain-incoming');
            html.style.backgroundColor = '';
            const bridge = document.querySelector('[data-curtain-bridge-style]');
            if (bridge) bridge.remove();
            const meta = document.querySelector('[data-curtain-theme]');
            if (meta) meta.remove();
            // STEP 3: cleanup after fall completes.
            setTimeout(() => { curtain.dataset.state = ''; }, 1100);
          }, 180);
        });
      });
    }
    document.addEventListener('click', (e) => {
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      const a = e.target.closest('a[href]');
      if (!a) return;
      if (a.target && a.target !== '_self') return;
      const href = a.getAttribute('href') || '';
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;
      if (a.hasAttribute('download')) return;
      let url;
      try { url = new URL(a.href, window.location.href); } catch (_) { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return;
      if (a.closest('[data-no-curtain]')) return;
      e.preventDefault();
      curtain.dataset.state = 'rising';
      sessionStorage.setItem('voyage-curtain-pending', 'true');
      // Navy bridge — paint <html> and <body> navy AT the moment the
      // curtain visually reaches full coverage. ANY browser blank-frame
      // between unload and next paint is then navy, not white.
      setTimeout(() => {
        html.style.backgroundColor = '#002F5B';
        document.body.style.backgroundColor = '#002F5B';
        document.body.dataset.curtainBridging = 'true';
      }, 580);
      // Force the curtain to its FINAL position with inline style just
      // before navigation. This guarantees the View Transitions API
      // captures a fully-covered screenshot even if the rise animation
      // hasn't quite landed yet — preventing the tiny content sliver
      // at the top that reads as a white flash.
      setTimeout(() => {
        curtain.style.animation = 'none';
        curtain.style.transform = 'translateY(0)';
      }, 880);
      // Navigate AFTER the rise has had time to fully complete and the
      // browser has painted the snapped final state.
      setTimeout(() => { window.location.href = a.href; }, 940);
    }, true);

    // Back/forward (bfcache) restore handler. When the user hits the
    // browser back button — especially on iOS Safari and Chrome —
    // the page is often restored from the back/forward cache with
    // the DOM exactly as it was at unload: curtain at translateY(0),
    // navy body background, inline animation:none. Without this
    // reset the user sees a stuck navy curtain on the page they
    // navigated back TO. Fire on every pageshow as defense in
    // depth; `persisted === true` is the bfcache-specific case.
    const resetCurtain = () => {
      curtain.dataset.state = '';
      curtain.style.animation = '';
      curtain.style.transform = '';
      html.classList.remove('curtain-incoming');
      html.style.backgroundColor = '';
      document.body.style.backgroundColor = '';
      delete document.body.dataset.curtainBridging;
      sessionStorage.removeItem('voyage-curtain-pending');
      const bridge = document.querySelector('[data-curtain-bridge-style]');
      if (bridge) bridge.remove();
      const meta = document.querySelector('[data-curtain-theme]');
      if (meta) meta.remove();
    };
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) resetCurtain();
    });
    // Same hazard when the user uses keyboard/mouse forward/back —
    // popstate fires on history navigation that doesn't trigger a
    // full page load (rare for our case, but covers SPA-ish flows).
    window.addEventListener('popstate', resetCurtain);
  })();

  /* ── Studio live clock + seasonal voyage status ──────────
     Small footer affectation: shows the current time and the
     house's current voyage destination. Updates every 30s.
     Psychology: liking + atmosphere — the brand feels alive. */
  (function () {
    const status = document.querySelector('[data-studio-status]');
    if (!status) return;
    const timeEl = status.querySelector('[data-studio-time]');
    const voyageEl = status.querySelector('[data-studio-voyage]');
    // Abbara rotation: Mediterranean ports per season — the house's
    // ship calls at a different port each quarter, narrated as the
    // current destination.
    const voyages = [
      { months: [11, 0, 1],  copy: 'Currently sailing east · Winter MMXXVI' },     // Dec-Feb
      { months: [2, 3, 4],   copy: 'Currently sailing along the coast · Spring MMXXVI' },   // Mar-May
      { months: [5, 6, 7],   copy: 'Currently sailing · Summer MMXXVI' }, // Jun-Aug
      { months: [8, 9, 10],  copy: 'Currently sailing the inland sea · Autumn MMXXVI' },     // Sep-Nov
    ];
    const update = () => {
      const now = new Date();
      // local time — Europe/Rome via Intl formatter so it works
      // even if the user's clock is in a different timezone.
      try {
        const fmt = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/Rome',
          hour: '2-digit', minute: '2-digit', hour12: false,
        });
        if (timeEl) timeEl.textContent = fmt.format(now) + ' CET';
      } catch (_) {
        // Fallback: user's local time, no timezone label
        const h = String(now.getHours()).padStart(2, '0');
        const m = String(now.getMinutes()).padStart(2, '0');
        if (timeEl) timeEl.textContent = h + ':' + m;
      }
      if (voyageEl) {
        const month = now.getMonth();
        const found = voyages.find((v) => v.months.includes(month));
        if (found) voyageEl.textContent = found.copy;
      }
    };
    update();
    setInterval(update, 30000); // every 30 seconds
  })();

  /* ── Recently viewed pieces ────────────────────────────────
     Tracks the last N products the customer opened. Renders a small
     "Continue browsing" strip inside the cart drawer footer when
     present, so the moment they consider checkout they see things
     they've already shown interest in. Psychology: commitment &
     consistency — re-exposing prior interest lifts conversion. */
  (function () {
    const KEY = 'voyage-recently-viewed';
    const MAX = 4;
    const read = () => {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
      catch (_) { return []; }
    };
    const write = (arr) => {
      try { localStorage.setItem(KEY, JSON.stringify(arr.slice(0, MAX))); }
      catch (_) {}
    };
    // If we're on a /products/* page, push it to the top of the list.
    const path = window.location.pathname;
    const pdpMatch = path.match(/^\/products\/([\w-]+)/);
    if (pdpMatch) {
      const handle = pdpMatch[1];
      const card = {
        handle,
        title: document.querySelector('h1, .pdp__title, .product__title')?.textContent.trim() || handle.replace(/-/g, ' '),
        url: path,
        img: document.querySelector('.pdp__main img, .product__media img, [data-product-image] img, main img')?.src || '',
      };
      const prev = read().filter((p) => p.handle !== handle);
      write([card, ...prev]);
    }
    // Render strip into the cart drawer's footer if there are entries.
    const renderInto = () => {
      const drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer) return;
      const items = read();
      if (!items.length) return;
      // Don't duplicate if already rendered.
      if (drawer.querySelector('[data-recently-viewed]')) return;
      // Render INSIDE the scrollable item list (at its end) — as a sibling of the
      // footer it inflated the drawer's fixed chrome past the viewport on mobile,
      // pushing checkout/continue off-screen with no way to scroll to them.
      const scrollArea = drawer.querySelector('[data-cart-scroll]');
      const slot = scrollArea || drawer.querySelector('.drawer__footer, [data-cart-footer]') || drawer.lastElementChild;
      if (!slot) return;
      const wrap = document.createElement('div');
      wrap.className = 'recently-viewed';
      wrap.setAttribute('data-recently-viewed', '');
      wrap.innerHTML = `
        <div class="recently-viewed__head">Continue browsing</div>
        <div class="recently-viewed__row">
          ${items.map((p) => `
            <a class="recently-viewed__item" href="${p.url}">
              ${p.img ? `<img src="${p.img}" alt="${(p.title || '').replace(/"/g, '&quot;')}" loading="lazy">` : '<span class="recently-viewed__ph"></span>'}
              <span class="recently-viewed__name">${(p.title || '').replace(/</g, '&lt;')}</span>
            </a>
          `).join('')}
        </div>
      `;
      if (scrollArea) scrollArea.appendChild(wrap);
      else slot.parentElement.insertBefore(wrap, slot);
    };
    // Initial + re-render after cart refresh (drawer rebuilds itself).
    renderInto();
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-open]')) {
        setTimeout(renderInto, 50);
      }
    });
  })();

  /* ── considered time-of-day greeting ──────────────────────────
     Replaces one announcement-bar marquee message with a
     bilingual greeting based on the user's local time. */
  (function () {
    const announce = document.querySelector('.announce-wrap, .announce__track, [data-announce-track]');
    if (!announce) return;
    const items = announce.querySelectorAll('.announce__item, [data-announce-item]');
    if (!items.length) return;
    const hr = new Date().getHours();
    let greeting;
    if (hr >= 5 && hr < 12)        greeting = 'Buongiorno · Good morning from studio';
    else if (hr >= 12 && hr < 18)  greeting = 'Buon pomeriggio · Good afternoon from studio';
    else if (hr >= 18 && hr < 23)  greeting = 'Buonasera · Good evening from studio';
    else                           greeting = 'Buonanotte · Notes from a quiet studio';
    // Replace the LAST visible announcement item so the greeting
    // closes the rotation — sits next to "EST · MMXXVI".
    const target = items[items.length - 1];
    if (target) target.textContent = greeting;
  })();

  /* ── Product-card hover icons → Quick View modal + Wishlist ─
     Triggered from any element with [data-pcard-quickview] on a
     product card (eye icon top-left, "Select Options" button at
     bottom). Fetches the rendered quick-view section via the
     Section Rendering API and injects it into the modal body. */
  (function () {
    const modal = document.querySelector('[data-quickview]');
    if (!modal) return;
    const bodyEl = modal.querySelector('[data-quickview-body]');

    // Merchant-editable copy from Theme Settings → Quick view.
    const qvCfg = window.abbaraQuickview || {};
    const loadingText = qvCfg.loadingText || 'Loading…';
    const errorText   = qvCfg.errorText   || 'Sorry — could not load this piece.';
    if (qvCfg.ariaLabel) modal.setAttribute('aria-label', qvCfg.ariaLabel);

    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

    const openQuickView = async (handle) => {
      if (!handle) return;
      bodyEl.innerHTML = '<div class="quickview__loading">' + escapeHtml(loadingText) + '</div>';
      modal.dataset.open = 'true';
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      try {
        const res = await fetch(`/products/${encodeURIComponent(handle)}?section_id=quick-view`, {
          credentials: 'same-origin',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        bodyEl.innerHTML = html;
        initQuickViewPicker(bodyEl);
      } catch (err) {
        bodyEl.innerHTML = '<p style="padding:40px;text-align:center;color:var(--muted);font-size:12px;letter-spacing:0.18em;text-transform:uppercase">' + escapeHtml(errorText) + '</p>';
      }
    };

    const closeQuickView = () => {
      if (modal.dataset.open !== 'true') return;
      modal.dataset.open = 'false';
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    // Variant-picker wiring inside the modal (same pattern as the PDP)
    const initQuickViewPicker = (root) => {
      const form = root.querySelector('[data-quickview-form]');
      if (!form) return;
      const idInput = form.querySelector('[data-quickview-variant-id]');
      const priceEl = root.querySelector('[data-quickview-price]');
      const qtyInput = form.querySelector('[data-quickview-qty]');
      const dec = form.querySelector('[data-quickview-qty-down]');
      const inc = form.querySelector('[data-quickview-qty-up]');
      let variants = [];
      const dataEl = root.querySelector('#quickview-variants-data');
      if (dataEl) {
        try { variants = JSON.parse(dataEl.textContent); } catch (_) {}
      }
      const fmtMoney = (cents) => {
        if (window.abbaraMoney) return window.abbaraMoney.format(cents);
        const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || '';
        return cur + ' ' + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
      };
      const currentSelections = () => {
        const out = {};
        form.querySelectorAll('input[name^="options["]:checked').forEach((r) => {
          const name = r.name.match(/options\[(.*)\]/)?.[1];
          if (name) out[name] = r.value;
        });
        return out;
      };
      const findVariant = (sel) => variants.find((v) => Object.keys(sel).every((k) => v.options[k] === sel[k]));
      // Keep the ATC button in lock-step with the selected combo's
      // availability — switching to a sold-out variant disables it and
      // swaps the label, mirroring the server-rendered initial state.
      const atcBtn = form.querySelector('[data-quickview-atc]');
      const atcInStockLabel = (atcBtn && !atcBtn.disabled) ? atcBtn.textContent : 'Add to Cart';
      const mediaImg = root.querySelector('[data-quickview-media-img]');
      let media = [];
      const mediaEl = root.querySelector('#quickview-media-data');
      if (mediaEl) { try { media = JSON.parse(mediaEl.textContent); } catch (_) {} }
      const update = () => {
        const sel = currentSelections();
        const v = findVariant(sel);
        if (!v) return;
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.textContent = fmtMoney(v.price);
        if (atcBtn) {
          atcBtn.disabled = v.available === false;
          atcBtn.textContent = v.available === false ? 'Sold Out' : atcInStockLabel;
        }
        // Swap the image to the selected colour: first try an image whose alt
        // text matches the colour (same tags as the PDP), then the variant's
        // own assigned image. No match → leave the current image untouched.
        if (mediaImg) {
          const colorVal = (sel.Color || sel.Colour || '').trim().toLowerCase();
          let src = null;
          if (colorVal && media.length) {
            const hit = media.find((m) => (m.color || '').includes(colorVal));
            if (hit) src = hit.url;
          }
          if (!src && v.image) src = v.image;
          if (src && src !== mediaImg.getAttribute('src')) mediaImg.src = src;
        }
      };
      form.addEventListener('change', update);
      if (dec && qtyInput) dec.addEventListener('click', () => {
        qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
      });
      if (inc && qtyInput) inc.addEventListener('click', () => {
        qtyInput.value = parseInt(qtyInput.value || '1', 10) + 1;
      });
      update();
    };

    // Click delegation — handles eye icon, "Select Options" button.
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-pcard-quickview]');
      if (!trigger) return;
      e.preventDefault();
      e.stopPropagation();
      openQuickView(trigger.dataset.handle);
    });

    // Close handlers — × button, backdrop, Esc.
    modal.addEventListener('click', (e) => {
      if (e.target.closest('[data-quickview-close]')) closeQuickView();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeQuickView();
    });

    /* ── Wishlist (localStorage + slide-out drawer) ───────────
       Stores product HANDLES so the drawer can fetch + render each
       saved product on demand. Hearts on product cards AND the PDP
       toggle the same list. A header heart icon opens the drawer. */
    (function () {
      const WISH_KEY = 'abbara-wishlist';
      const getList = () => {
        try { const v = JSON.parse(localStorage.getItem(WISH_KEY) || '[]'); return Array.isArray(v) ? v : []; }
        catch (_) { return []; }
      };
      const setList = (list) => {
        try { localStorage.setItem(WISH_KEY, JSON.stringify(list)); } catch (_) {}
      };
      const fmtMoney = (cents) => {
        if (window.abbaraMoney) return window.abbaraMoney.format(cents);
        const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AED';
        return cur + ' ' + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
      };

      const wDrawer   = document.querySelector('[data-wishlist-drawer]');
      const wBackdrop = document.querySelector('[data-wishlist-backdrop]');
      const wItems    = wDrawer && wDrawer.querySelector('[data-wishlist-items]');
      const wEmpty    = wDrawer && wDrawer.querySelector('[data-wishlist-empty]');
      const cartDrawer   = document.querySelector('[data-cart-drawer]');
      const cartBackdrop = document.querySelector('[data-cart-backdrop]');

      // Paint every heart (cards + PDP) based on the saved list.
      const paintHearts = () => {
        const list = getList();
        document.querySelectorAll('[data-pcard-wishlist], [data-pdp-wishlist]').forEach((btn) => {
          btn.dataset.active = list.includes(btn.dataset.handle) ? 'true' : 'false';
        });
      };
      // Header badge count.
      const updateCount = () => {
        const n = getList().length;
        document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
          el.textContent = String(n);
          el.dataset.empty = n === 0 ? 'true' : 'false';
        });
      };

      // Render the saved products into the drawer (fetch each as JSON).
      let rendering = false;
      const renderWishlist = async () => {
        if (!wItems || rendering) return;
        rendering = true;
        const list = getList();
        if (!list.length) {
          wItems.innerHTML = '';
          if (wEmpty) wEmpty.hidden = false;
          rendering = false;
          return;
        }
        if (wEmpty) wEmpty.hidden = true;
        wItems.innerHTML = '<div class="wishlist-drawer__loading">…</div>';
        const cards = [];
        const stale = [];
        for (const handle of list) {
          try {
            const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
            if (!res.ok) { stale.push(handle); continue; }
            const pr = await res.json();
            const img = pr.featured_image || (pr.images && pr.images[0]) || '';
            const imgSrc = img ? (img.indexOf('//') === 0 ? img : img) : '';
            cards.push(
              '<article class="wishlist-drawer__item" data-wishlist-row="' + handle + '">' +
                '<a href="' + pr.url + '" class="wishlist-drawer__media">' +
                  (imgSrc ? '<img src="' + imgSrc + '&width=200" alt="" loading="lazy">' : '') +
                '</a>' +
                '<div class="wishlist-drawer__info">' +
                  '<a href="' + pr.url + '" class="wishlist-drawer__title">' + pr.title + '</a>' +
                  '<div class="wishlist-drawer__price">' + fmtMoney(pr.price) + '</div>' +
                  '<a href="' + pr.url + '" class="wishlist-drawer__view">View product →</a>' +
                '</div>' +
                '<button type="button" class="wishlist-drawer__remove" data-wishlist-remove="' + handle + '" aria-label="Remove from wishlist">×</button>' +
              '</article>'
            );
          } catch (_) { stale.push(handle); }
        }
        // Prune any handles that no longer resolve (deleted/unpublished).
        if (stale.length) {
          const cleaned = getList().filter((h) => stale.indexOf(h) === -1);
          setList(cleaned); updateCount(); paintHearts();
        }
        if (!cards.length) {
          wItems.innerHTML = '';
          if (wEmpty) wEmpty.hidden = false;
        } else {
          wItems.innerHTML = cards.join('');
        }
        rendering = false;
      };

      // Open / close (mutually exclusive with the cart drawer).
      const openWishlist = () => {
        if (cartDrawer)   cartDrawer.dataset.open = 'false';
        if (cartBackdrop) cartBackdrop.dataset.open = 'false';
        if (wDrawer)   wDrawer.dataset.open = 'true';
        if (wBackdrop) wBackdrop.dataset.open = 'true';
        document.body.style.overflow = 'hidden';
        renderWishlist();
      };
      const closeWishlist = () => {
        if (wDrawer)   wDrawer.dataset.open = 'false';
        if (wBackdrop) wBackdrop.dataset.open = 'false';
        document.body.style.overflow = '';
      };

      // Toggle a product in/out of the wishlist.
      const toggle = (handle, btn) => {
        if (!handle) return;
        const list = getList();
        const idx = list.indexOf(handle);
        if (idx >= 0) list.splice(idx, 1); else list.push(handle);
        setList(list);
        paintHearts();
        updateCount();
        if (wDrawer && wDrawer.dataset.open === 'true') renderWishlist();
      };

      // Clicks: hearts (cards + PDP), open, close, remove.
      document.addEventListener('click', (e) => {
        const heart = e.target.closest('[data-pcard-wishlist], [data-pdp-wishlist]');
        if (heart) { e.preventDefault(); e.stopPropagation(); toggle(heart.dataset.handle, heart); return; }
        if (e.target.closest('[data-wishlist-open]'))  { e.preventDefault(); openWishlist();  return; }
        if (e.target.closest('[data-wishlist-close]')) { e.preventDefault(); closeWishlist(); return; }
        if (e.target.closest('[data-cart-open]')) {
          // Opening the cart: hide the wishlist drawer but leave body scroll-
          // lock to the cart's own openCart (don't reset overflow here).
          if (wDrawer)   wDrawer.dataset.open = 'false';
          if (wBackdrop) wBackdrop.dataset.open = 'false';
        }
        const rm = e.target.closest('[data-wishlist-remove]');
        if (rm) { e.preventDefault(); toggle(rm.dataset.wishlistRemove); }
      });
      if (wBackdrop) wBackdrop.addEventListener('click', closeWishlist);
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && wDrawer && wDrawer.dataset.open === 'true') closeWishlist();
      });

      // Initial paint.
      paintHearts();
      updateCount();
      window.abbaraWishlist = { toggle, open: openWishlist, close: closeWishlist };
    })();
  })();

  /* ── Scroll blur-to-clear (product / category images) ───────
     CSS sets every targeted image to filter: blur(8px). This
     IntersectionObserver adds `is-revealed` when the image first
     enters the viewport — CSS transition fades the blur to 0. Once
     revealed, the class stays so the image doesn't flicker on
     scroll-back. Works in every browser with IntersectionObserver
     (effectively every modern browser).

     Reduced-motion users have `filter: none` from CSS via the
     `@media (prefers-reduced-motion: reduce)` block, so they see
     sharp images from the start — observer is a no-op for them. */
  (function () {
    if (!('IntersectionObserver' in window)) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const targets = document.querySelectorAll(
      '.pcard__media-stack img, .archive-card__media img'
    );
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        });
      },
      {
        // Fire as soon as any pixel of the image enters viewport.
        // The blur dissolves over the next 700ms; by the time the
        // user scrolls another few hundred pixels, the image is
        // fully clear.
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.01,
      }
    );
    targets.forEach((img) => io.observe(img));
  })();

  /* ── Tab-title "come back" hook ────────────────────────────
     When the visitor switches to another tab/window, the document
     title (the visible browser-tab label) rotates through merchant-
     editable messages on a configurable interval — restored when
     they return.

     Behavior + copy come from window.abbaraTabRecall emitted by
     layout/theme.liquid based on Theme Settings → Tab title recall.
     Hardcoded fallbacks preserve the original behavior if the config
     object is missing for any reason. */
  (function () {
    const cfg = window.abbaraTabRecall || {};
    if (cfg.enable === false) return;
    const messages = (cfg.messages && cfg.messages.length) ? cfg.messages : [
      '← Don\'t leave! Come back',
      '← Selling fast · only a few left',
      '← Hurry · stock running low',
    ];
    const firstDelay = typeof cfg.delay === 'number'    ? cfg.delay    : 1000;
    const rotateMs   = typeof cfg.interval === 'number' ? cfg.interval : 1500;

    const original = document.title;
    let i = 0;
    let firstSwap = null;
    let rotation  = null;
    const swap = () => {
      document.title = messages[i % messages.length];
      i++;
    };
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        firstSwap = setTimeout(() => {
          swap();
          rotation = setInterval(swap, rotateMs);
        }, firstDelay);
      } else {
        if (firstSwap) { clearTimeout(firstSwap);  firstSwap = null; }
        if (rotation)  { clearInterval(rotation);  rotation  = null; }
        document.title = original;
        i = 0;
      }
    });
  })();

  /* ── Search panel (live results + total count + view-all) ──
     Opens a full-width panel below the nav with live results fetched
     from Shopify's predictive search endpoint:
     /search/suggest.json?q=…&resources[type]=product,collection,article
     The response carries products, collections, and articles in one
     round-trip, so the panel can render a mixed result set.
     A "View all results →" link routes to the styled /search?q=… page
     (templates/search.liquid).
     Also tracks the visitor's last 5 successful searches in
     localStorage. Mirrors the cart-drawer open/close pattern. */
  (function () {
    const panel   = document.querySelector('[data-search-panel]');
    const input   = document.querySelector('[data-search-input]');
    const results = document.querySelector('[data-search-results]');
    const meta    = document.querySelector('[data-search-meta]');
    const countEl = document.querySelector('[data-search-count]');
    const viewAll = document.querySelector('[data-search-view-all]');
    const recentBox  = document.querySelector('[data-search-recent]');
    const recentList = document.querySelector('[data-search-recent-list]');
    const triggers = document.querySelectorAll('[data-search-open]');
    const closers  = document.querySelectorAll('[data-search-close]');
    if (!panel || !input || !results) return;

    const STORAGE_KEY = 'voyage-recent-searches';
    const MAX_RECENT  = 5;

    // Snapshot the server-rendered default-state markup so we can
    // restore it when the input is cleared.
    const defaultHTML = results.innerHTML;

    const getRecent = () => {
      try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); }
      catch (e) { return []; }
    };
    const saveRecent = (term) => {
      const t = term.trim();
      if (!t) return;
      let list = getRecent().filter(x => x.toLowerCase() !== t.toLowerCase());
      list.unshift(t);
      if (list.length > MAX_RECENT) list = list.slice(0, MAX_RECENT);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); } catch (e) {}
    };
    const escapeAttr = (s) => String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const renderRecent = () => {
      if (!recentBox || !recentList) return;
      const list = getRecent();
      if (!list.length) { recentBox.hidden = true; return; }
      recentBox.hidden = false;
      recentList.innerHTML = list.map(t =>
        `<li><a href="#" data-search-recent-term="${escapeAttr(t)}">${escapeAttr(t)}</a></li>`
      ).join('');
    };

    const hideMeta = () => { if (meta) meta.hidden = true; };

    const navEl = document.querySelector('.nav');
    const innerEl = panel.querySelector('.search-panel__inner');
    // Anchor the panel to the nav's REAL bottom (which shifts to ~106px when the
    // announce bar has revealed on scroll) so it never opens behind the nav.
    const pinPanel = () => {
      if (!navEl) return;
      const b = Math.max(0, Math.round(navEl.getBoundingClientRect().bottom));
      panel.style.setProperty('--search-top', b + 'px');
    };
    const setOpen = (open) => {
      if (open) pinPanel();
      panel.dataset.open = open ? 'true' : 'false';
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      triggers.forEach(t => t.setAttribute('aria-expanded', open ? 'true' : 'false'));
      document.body.dataset.searchOpen = open ? 'true' : 'false';
      if (open) {
        renderRecent();
        // Defer focus until after the slide-down so iOS doesn't scroll-jump.
        setTimeout(() => input.focus({ preventScroll: true }), 60);
      } else {
        input.blur(); // drop the on-screen keyboard when the panel closes
      }
    };
    window.addEventListener('resize', () => { if (panel.dataset.open === 'true') pinPanel(); });
    // Tap anywhere outside the search content closes it.
    document.addEventListener('pointerdown', (e) => {
      if (panel.dataset.open !== 'true') return;
      if (innerEl && innerEl.contains(e.target)) return;   // inside the panel content
      if (e.target.closest && e.target.closest('[data-search-open]')) return; // the trigger toggles itself
      setOpen(false);
    });
    // Vertical swipe (up or down) anywhere closes the panel + dismisses the keyboard.
    // Horizontal swipes are left alone so the product row can still scroll.
    let _sx = 0, _sy = 0, _swept = false;
    document.addEventListener('touchstart', (e) => {
      if (panel.dataset.open !== 'true' || !e.touches[0]) return;
      _swept = false; _sx = e.touches[0].clientX; _sy = e.touches[0].clientY;
    }, { passive: true });
    document.addEventListener('touchmove', (e) => {
      if (panel.dataset.open !== 'true' || _swept || !e.touches[0]) return;
      const dy = e.touches[0].clientY - _sy;
      const dx = e.touches[0].clientX - _sx;
      if (Math.abs(dy) > 36 && Math.abs(dy) > Math.abs(dx) * 1.2) { _swept = true; setOpen(false); }
    }, { passive: true });

    // Search icon toggles — click to open if closed, click to close if open.
    triggers.forEach(t => t.addEventListener('click', () => {
      setOpen(panel.dataset.open !== 'true');
    }));
    closers.forEach(c => c.addEventListener('click', () => setOpen(false)));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.dataset.open === 'true') setOpen(false);
    });

    // Click a recent-search term → populate input + trigger search.
    if (recentList) {
      recentList.addEventListener('click', (e) => {
        const a = e.target.closest('[data-search-recent-term]');
        if (!a) return;
        e.preventDefault();
        input.value = a.dataset.searchRecentTerm;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus({ preventScroll: true });
      });
    }

    // Enter key → navigate to the styled /search page (templates/search.liquid).
    // No longer redirects to Shopify's default unstyled layout — that page
    // is overridden by our template. Visitors get the PLP-style results grid.
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value.trim()) {
        e.preventDefault();
        saveRecent(input.value.trim());
        window.location.href = '/search?q=' + encodeURIComponent(input.value.trim()) + '&type=product';
      }
    });

    // Debounced live fetch via Shopify's predictive search API.
    // `timer` debounces the keystroke → fetch trigger; `inflight` lets
    // us actively cancel an in-flight fetch when the visitor types more
    // or clears, so stale responses can't overwrite the current state.
    let timer    = null;
    let inflight = null;

    const renderProductCard = (p) => {
      const img = (p.featured_image && p.featured_image.url)
        ? `<img src="${escapeAttr(p.featured_image.url)}" alt="${escapeAttr(p.title || '')}" loading="lazy">`
        : '';
      const price = (p.price && typeof p.price === 'string') ? p.price : '';
      return `
        <a class="search-panel__card" href="${escapeAttr(p.url || '#')}">
          <div class="search-panel__card-media">${img}</div>
          <div class="search-panel__card-meta">
            <p class="search-panel__card-name">${escapeAttr(p.title || '')}</p>
            ${p.product_type ? `<p class="search-panel__card-type">${escapeAttr(p.product_type)}</p>` : ''}
            ${price ? `<p class="search-panel__card-price">${escapeAttr(price)}</p>` : ''}
          </div>
        </a>`;
    };

    const renderLinkRow = (item, kind) => {
      return `
        <a class="search-panel__row" href="${escapeAttr(item.url || '#')}" data-search-row-kind="${escapeAttr(kind)}">
          <span class="search-panel__row-kind">${escapeAttr(kind)}</span>
          <span class="search-panel__row-title">${escapeAttr(item.title || '')}</span>
        </a>`;
    };

    // Shopify's predictive search returns prices in minor units (no
    // formatting) on the product object. The endpoint also returns
    // a pre-formatted `price` string when called as suggest.json — we
    // pass it through unchanged.
    const SUGGEST_URL = '/search/suggest.json';
    const buildUrl = (q) => {
      const params = new URLSearchParams();
      params.set('q', q);
      params.set('resources[type]', 'product,collection,article');
      params.set('resources[limit]', '6');
      params.set('resources[options][unavailable_products]', 'last');
      params.set('resources[options][fields]', 'title,product_type,variants.title,vendor');
      return `${SUGGEST_URL}?${params.toString()}`;
    };

    input.addEventListener('input', () => {
      const q = input.value.trim();
      clearTimeout(timer);
      // Cancel any in-flight fetch so a stale "cotton" response can't
      // win after the visitor backspaces to "co" or clears entirely.
      if (inflight) { inflight.abort(); inflight = null; }
      if (!q) {
        // Restore default state: featured products + hide meta footer.
        results.innerHTML = defaultHTML;
        hideMeta();
        return;
      }
      timer = setTimeout(() => {
        inflight = new AbortController();
        fetch(buildUrl(q), {
          signal: inflight.signal,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(r => r.json())
          .then(data => {
            // Belt-and-braces: even if abort raced (microtask already
            // queued), ignore responses whose query is no longer current.
            if (input.value.trim() !== q) return;
            const resources    = (data && data.resources) || {};
            const buckets      = (resources.results) || {};
            const products     = buckets.products    || [];
            const collections  = buckets.collections || [];
            const articles     = buckets.articles    || [];
            const total = products.length + collections.length + articles.length;
            if (!total) {
              const emptyText = (window.abbaraSearch && window.abbaraSearch.emptyText) || 'No pieces match — try a different term.';
              const safeEmpty = String(emptyText).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
              results.innerHTML = '<p class="search-panel__empty">' + safeEmpty + '</p>';
              hideMeta();
            } else {
              const html = [];
              if (products.length) {
                html.push(products.map(renderProductCard).join(''));
              }
              if (collections.length || articles.length) {
                html.push('<div class="search-panel__rows">');
                collections.forEach((c) => html.push(renderLinkRow(c, 'Collection')));
                articles.forEach((a) => html.push(renderLinkRow(a, 'Journal')));
                html.push('</div>');
              }
              results.innerHTML = html.join('');
              if (meta && countEl && viewAll) {
                countEl.textContent = `${total} ${total === 1 ? 'result' : 'results'}`;
                viewAll.href = `/search?q=${encodeURIComponent(q)}&type=product`;
                meta.hidden = false;
              }
              // Save successful queries to recent (skip zero-result terms).
              saveRecent(q);
            }
          })
          .catch((err) => {
            // AbortError is expected — we cancelled deliberately on keystroke.
            if (err && err.name === 'AbortError') return;
            // Other errors: keep last good results visible on network blips.
          });
      }, 200);
    });
  })();

  /* ─────────────────────────────────────────────────────────
     Product recommendations (related + complementary)

     Sections rendered with [data-product-recommendations] start
     empty on first paint (Shopify only populates the `recommendations`
     Liquid object when the request URL has the `intent` param).
     Here we fetch each section's URL through the Section Rendering
     API, extract its inner HTML, and swap it in. The `reveal`
     wrappers in the response keep the fade-in stagger consistent.

     The Web Share API for the [data-pdp-share] button is wired
     in this same module since it's PDP-adjacent.
     ───────────────────────────────────────────────────────── */
  (function productRecommendations() {
    const sections = document.querySelectorAll('[data-product-recommendations]');
    if (!sections.length) return;

    sections.forEach(section => {
      const url = section.getAttribute('data-url');
      if (!url) return;

      fetch(url)
        .then(r => r.text())
        .then(html => {
          const tmp = document.createElement('div');
          tmp.innerHTML = html.trim();
          // The fetched HTML has a single root <section> wrapper from the
          // section file. Replace this section's contents with that root's
          // children to avoid double-wrapping.
          const root = tmp.querySelector('[data-product-recommendations]');
          if (root) {
            section.innerHTML = root.innerHTML;

            // Reveal the new product card wrappers (fade-in stagger).
            section.querySelectorAll('.reveal').forEach((el, i) => {
              setTimeout(() => el.setAttribute('data-in', 'true'), i * 30);
            });

            // Mark newly injected images as revealed so the CSS blur-to-clear
            // transition clears. The IntersectionObserver that does this for
            // initial page load already finished scanning the DOM before this
            // XHR ran, so newly added images would stay blurred otherwise.
            section.querySelectorAll(
              '.pcard__media-stack img, .archive-card__media img'
            ).forEach(img => img.classList.add('is-revealed'));
          }
        })
        .catch(() => {
          // Network failure — section stays empty. Better silent than broken.
        });
    });
  })();

  /* Back-to-top floating button.
     Appears once the visitor has scrolled past 600px. Uses [hidden]
     for the resting state and data-visible="true" as the CSS-driven
     show flag so the button can transition opacity smoothly while
     still being removed from the a11y tree when not in view. */
  (function backToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    const THRESHOLD = 600;
    let ticking = false;
    let hideTimer = null;
    const update = () => {
      ticking = false;
      if (window.scrollY > THRESHOLD) {
        if (hideTimer) { clearTimeout(hideTimer); hideTimer = null; }
        if (btn.hasAttribute('hidden')) btn.removeAttribute('hidden');
        btn.setAttribute('data-visible', 'true');
      } else {
        btn.setAttribute('data-visible', 'false');
        // Re-hide after the fade-out completes so it leaves the a11y tree.
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          if (btn.getAttribute('data-visible') === 'false') {
            btn.setAttribute('hidden', '');
          }
        }, 260);
      }
    };
    window.addEventListener('scroll', () => {
      if (!ticking) {
        window.requestAnimationFrame(update);
        ticking = true;
      }
    }, { passive: true });
    btn.addEventListener('click', () => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
    update();
  })();

  /* Countdown ticker.
     One IIFE handles every [data-countdown] section on the page —
     each section provides its own ISO 8601 target via
     data-target-datetime. Ticks once per second; when the target has
     passed, the timer is hidden and the expired message takes its
     place. Invalid dates are treated as expired immediately so the
     merchant sees their fallback copy rather than NaN cells. */
  (function countdownTicker() {
    const sections = document.querySelectorAll('[data-countdown]');
    if (!sections.length) return;
    const pad = (n) => String(n).padStart(2, '0');
    const instances = [];
    sections.forEach((section) => {
      const raw = section.getAttribute('data-target-datetime');
      const target = raw ? new Date(raw).getTime() : NaN;
      const timer   = section.querySelector('[data-countdown-timer]');
      const expired = section.querySelector('[data-countdown-expired]');
      const cells = {
        d: section.querySelector('[data-countdown-d]'),
        h: section.querySelector('[data-countdown-h]'),
        m: section.querySelector('[data-countdown-m]'),
        s: section.querySelector('[data-countdown-s]')
      };
      instances.push({ target, timer, expired, cells, done: false });
    });
    const tick = () => {
      const now = Date.now();
      instances.forEach((i) => {
        if (i.done) return;
        const diff = i.target - now;
        if (!isFinite(i.target) || diff <= 0) {
          if (i.timer)   i.timer.setAttribute('hidden', '');
          if (i.expired) i.expired.removeAttribute('hidden');
          i.done = true;
          return;
        }
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        if (i.cells.d) i.cells.d.textContent = pad(d);
        if (i.cells.h) i.cells.h.textContent = pad(h);
        if (i.cells.m) i.cells.m.textContent = pad(m);
        if (i.cells.s) i.cells.s.textContent = pad(s);
      });
    };
    tick();
    setInterval(tick, 1000);
  })();

  /* ── Age verifier ─────────────────────────────────────────
     Splash gate driven by Theme Settings → Age verifier. Disabled by
     default. When enabled, the snippet `age-verifier.liquid` is rendered
     in the layout; this IIFE wires its DOB input, validates against the
     merchant-set minimum age, and persists the verified state in
     sessionStorage for the duration of the visit. */
  (function () {
    const cfg = window.abbaraAgeVerifier || {};
    if (!cfg.enable) return;
    const gate = document.querySelector('[data-age-verifier]');
    if (!gate) return;

    const KEY = 'abbara_age_verified';
    // Already verified this session? hide immediately.
    try {
      if (sessionStorage.getItem(KEY) === 'true') {
        gate.setAttribute('hidden', '');
        return;
      }
    } catch (_) { /* sessionStorage blocked — fall through and show gate */ }

    // Lock body scroll while gate is up.
    document.body.style.overflow = 'hidden';
    gate.setAttribute('data-open', 'true');

    const form     = gate.querySelector('[data-age-form]');
    const dobInput = gate.querySelector('[data-age-dob]');
    const declineBtn = gate.querySelector('[data-age-decline]');
    const errorEl  = gate.querySelector('[data-age-error]');
    const declineEl = gate.querySelector('[data-age-decline-message]');

    const minAge = typeof cfg.minAge === 'number' ? cfg.minAge : 21;

    const computeAge = (dobStr) => {
      const dob = new Date(dobStr);
      if (isNaN(dob.getTime())) return null;
      const today = new Date();
      let age = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
      return age;
    };

    const reveal = () => {
      try { sessionStorage.setItem(KEY, 'true'); } catch (_) {}
      gate.setAttribute('data-open', 'false');
      gate.setAttribute('hidden', '');
      document.body.style.overflow = '';
    };

    const showDecline = () => {
      if (declineEl) {
        declineEl.textContent = cfg.declineMessage || 'We\'re unable to serve you at this time. Please come back when you are of legal age.';
        declineEl.removeAttribute('hidden');
      }
      if (form) form.setAttribute('hidden', '');
    };

    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        if (errorEl) errorEl.setAttribute('hidden', '');
        const age = computeAge(dobInput && dobInput.value);
        if (age === null) {
          if (errorEl) {
            errorEl.textContent = 'Please enter a valid date.';
            errorEl.removeAttribute('hidden');
          }
          return;
        }
        if (age >= minAge) {
          reveal();
        } else {
          showDecline();
        }
      });
    }

    if (declineBtn) {
      declineBtn.addEventListener('click', (e) => {
        e.preventDefault();
        showDecline();
      });
    }
  })();

  /* Native share + clipboard fallback for [data-pdp-share] */
  (function pdpShare() {
    const btn = document.querySelector('[data-pdp-share]');
    if (!btn) return;
    btn.addEventListener('click', async () => {
      const data = {
        title: document.title,
        text:  document.title,
        url:   window.location.href
      };
      try {
        if (navigator.share) {
          await navigator.share(data);
        } else if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(window.location.href);
          const orig = btn.textContent;
          btn.textContent = 'Link copied';
          setTimeout(() => { btn.textContent = orig; }, 1600);
        }
      } catch (_) {
        // user cancelled — silent
      }
    });
  })();
})();

/* ── Mobile footer accordions ─────────────────────────────────────
   On phones (≤768px) the footer link columns collapse into tap-to-
   expand accordions for a cleaner, less-cluttered layout. Social and
   payment columns stay open as simple rows. Desktop is untouched: all
   panels are forced visible and the +/– chrome is mobile-only CSS. */
(function footerAccordions() {
  const foot = document.querySelector('.footer--v2');
  if (!foot) return;

  foot.querySelectorAll('.footer__col').forEach((col) => {
    const h5 = col.querySelector(':scope > h5');
    if (!h5 || !h5.nextElementSibling) return;
    // Social / payment / newsletter columns stay open — not link lists.
    if (col.querySelector('.footer__social, .footer__payments, .footer__col-news')) return;

    col.classList.add('footer__col--acc');
    h5.setAttribute('role', 'button');
    h5.setAttribute('tabindex', '0');
    h5.setAttribute('aria-expanded', 'false');

    // Visibility is entirely CSS-driven: collapsed by default on mobile,
    // always shown on desktop (the collapse rule is inside a media query).
    // JS only flips the .is-open class.
    const toggle = () => {
      const open = col.classList.toggle('is-open');
      h5.setAttribute('aria-expanded', open ? 'true' : 'false');
    };
    h5.addEventListener('click', toggle);
    h5.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
  });
})();

/* ── Product-card colour reveal ────────────────────────────────────
   The card has two image layers: Layer A (cover, resting) and Layer B (the
   selected colour's photo, revealed on hover/swipe). Picking a colour swatch
   sets WHICH colour Layer B shows. Layer B is revealed by the CSS .pcard:hover
   rules on desktop, and by the .pcard--peek class on touch (swipe / swatch tap). */
const PCARD_HOVER = window.matchMedia && window.matchMedia('(hover: hover)').matches;

function pcardSetReveal(card, sw) {
  if (!card) return;
  const img = card.querySelector('.pcard__layer--hover img'); // Layer B
  if (img) {
    if (img.dataset.defaultSrc == null) {
      img.dataset.defaultSrc = img.getAttribute('src') || '';
      img.dataset.defaultSrcset = img.getAttribute('srcset') || '';
    }
    if (sw.dataset.srcset) img.srcset = sw.dataset.srcset;
    if (sw.dataset.image)  img.src = sw.dataset.image;
  }
  // Point the card's links at the chosen colour's variant, so clicking through
  // opens the product page on THAT colour (not the default).
  if (sw.dataset.url) {
    card.querySelectorAll('a.pcard__media, a.pcard__meta').forEach((a) => {
      if (a.dataset.defaultHref == null) a.dataset.defaultHref = a.getAttribute('href') || '';
      a.setAttribute('href', sw.dataset.url);
    });
  }
  card.querySelectorAll('[data-pcard-color]').forEach((s) => s.classList.toggle('is-active', s === sw));

  // Desktop only: when the pointer leaves the card, revert the image, the
  // links, and the active swatch. On touch the selection is sticky (no
  // mouseleave), so the chosen colour's link survives the tap-through.
  if (PCARD_HOVER && card.dataset.revealRevert !== '1') {
    card.dataset.revealRevert = '1';
    card.addEventListener('mouseleave', () => {
      const di = card.querySelector('.pcard__layer--hover img');
      if (di && di.dataset.defaultSrc != null) {
        if (di.dataset.defaultSrcset) di.srcset = di.dataset.defaultSrcset;
        di.src = di.dataset.defaultSrc;
      }
      card.querySelectorAll('a.pcard__media, a.pcard__meta').forEach((a) => {
        if (a.dataset.defaultHref != null) a.setAttribute('href', a.dataset.defaultHref);
      });
      card.querySelectorAll('[data-pcard-color]').forEach((s) => s.classList.remove('is-active'));
      card.classList.remove('pcard--peek');
    });
  }
}

/* Tap/click a swatch — capture phase + preventDefault/stopPropagation so it
   doesn't follow the card link or fire the page-transition curtain. On touch it
   also reveals Layer B (.pcard--peek), since there's no hover. */
document.addEventListener('click', (e) => {
  const sw = e.target.closest('[data-pcard-color]');
  if (!sw) return;
  e.preventDefault();
  e.stopPropagation();
  const card = sw.closest('.pcard');
  pcardSetReveal(card, sw);
  if (!PCARD_HOVER && card) card.classList.add('pcard--peek');
}, true);

/* Desktop: simply HOVERING a swatch previews that colour on Layer B. */
if (PCARD_HOVER) {
  document.addEventListener('mouseover', (e) => {
    const sw = e.target.closest('[data-pcard-color]');
    if (!sw) return;
    pcardSetReveal(sw.closest('.pcard'), sw);
  });
}

/* Touch: a horizontal swipe on a card image peeks the colour photo (Layer B);
   swipe back to return to the cover. Passive listeners so vertical scroll is
   never blocked — we only act when the gesture is clearly horizontal. */
if (!PCARD_HOVER) {
  let sx = 0, sy = 0, tracking = false;
  document.addEventListener('touchstart', (e) => {
    const media = e.target.closest('.pcard__media');
    if (!media) { tracking = false; return; }
    const t = e.touches[0];
    sx = t.clientX; sy = t.clientY; tracking = true;
  }, { passive: true });
  document.addEventListener('touchmove', (e) => {
    if (!tracking) return;
    const media = e.target.closest('.pcard__media');
    if (!media) { tracking = false; return; }
    const t = e.touches[0];
    const dx = t.clientX - sx, dy = t.clientY - sy;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      const card = media.closest('.pcard');
      if (card) card.classList.toggle('pcard--peek', dx < 0); // swipe left → peek, right → cover
      tracking = false;
    }
  }, { passive: true });
  document.addEventListener('touchend', () => { tracking = false; }, { passive: true });
}
